import React, { useState, useEffect, useCallback, useMemo } from 'react';
import OnboardingWizard from '../components/OnboardingWizard';
import AnnouncementsBanner from '../components/AnnouncementsBanner';
import TeacherHomeworkSection from '../components/TeacherHomeworkSection';
import TeacherCodingReview from '../components/TeacherCodingReview';
import ChatPanel from '../components/ChatPanel';
import TeacherAvailability from '../components/TeacherAvailability';
import ScheduleRequestsPanel from '../components/ScheduleRequestsPanel';
import PendingFeedbackDropdown from '../components/PendingFeedbackDropdown';
import ClassFeedbackForm from '../components/ClassFeedbackForm';
import AddAvailabilitySlideOver from '../components/AddAvailabilitySlideOver';
import NotificationBell from '../components/NotificationBell';
import MyFilesPage from '../components/MyFilesPage';
import FileUpload from '../components/FileUpload';
import TeacherRecordingsSection from '../components/TeacherRecordingsSection';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Home, Calendar, Users, UsersRound, IndianRupee, UserPlus, BarChart3, FileText,
  LogOut, GraduationCap, Star, Plus, Send, Paperclip, X, Check,
  Clock, Upload, User, Award, TrendingUp, ClipboardList, Share2,
  BookOpen, MessageSquare, CreditCard, Settings, Bell, CalendarClock, Target,
  ChevronRight, Sparkles, Trophy, CheckCircle, Zap, Eye, MessageCircle,
  Globe, ChevronLeft, RefreshCw, Video, ExternalLink, AlertTriangle, MonitorPlay, Mail,
  Link2, Trash2, Menu, Edit3, Search, Filter, ArrowRight, Layers, Code as CodeIcon, Folder
} from 'lucide-react';
import QuizManager from '../components/QuizManager';
import AIBuddyPanel from '../components/AIBuddyPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const COURSE_CATALOG = {
  'Coding 6-12 Years': ['Scratch', 'Thunkable', 'Game Development'],
  'Coding 13-16 Years': ['Python', 'Java', 'HTML & CSS', 'App Development', 'Py Game'],
  'GenAI': ['GenAI']
};

const MONTHS = [
  { value: '2025-01', label: 'January 2025' }, { value: '2025-02', label: 'February 2025' },
  { value: '2025-03', label: 'March 2025' }, { value: '2025-04', label: 'April 2025' },
  { value: '2025-05', label: 'May 2025' }, { value: '2025-06', label: 'June 2025' },
  { value: '2025-07', label: 'July 2025' }, { value: '2025-08', label: 'August 2025' },
  { value: '2025-09', label: 'September 2025' }, { value: '2025-10', label: 'October 2025' },
  { value: '2025-11', label: 'November 2025' }, { value: '2025-12', label: 'December 2025' },
  { value: '2026-01', label: 'January 2026' }, { value: '2026-02', label: 'February 2026' },
  { value: '2026-03', label: 'March 2026' }, { value: '2026-04', label: 'April 2026' },
  { value: '2026-05', label: 'May 2026' }, { value: '2026-06', label: 'June 2026' },
];

const QUOTES = [
  { text: 'Teaching is not about answering questions but about raising questions – opening doors for them in places that they could not imagine.', author: 'Terry Heick' },
  { text: 'The mediocre teacher tells. The good teacher explains. The superior teacher demonstrates. The great teacher inspires.', author: 'William Arthur Ward' },
  { text: 'Education is the most powerful weapon which you can use to change the world.', author: 'Nelson Mandela' },
];

const TIMEZONE_MAP = {
  'IST': 'Asia/Kolkata', 'EST': 'America/New_York', 'PST': 'America/Los_Angeles',
  'GMT': 'Europe/London', 'SGT': 'Asia/Singapore',
};

const convertTime = (time24, fromTZ, toTZ) => {
  try {
    if (!time24 || !fromTZ) return time24;
    const ianaFrom = TIMEZONE_MAP[fromTZ] || fromTZ;
    const ianaTo = TIMEZONE_MAP[toTZ] || toTZ;
    const [h, m] = time24.split(':').map(Number);
    const today = new Date();
    today.setHours(h, m, 0, 0);
    return today.toLocaleTimeString('en-US', { timeZone: ianaTo, hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return time24; }
};

const getCurrentTimeInTZ = (tz) => {
  try {
    const ianaZone = TIMEZONE_MAP[tz] || tz;
    return new Date().toLocaleTimeString('en-US', { timeZone: ianaZone, hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ''; }
};

// Join is enabled 1 minute before class start time and during class (until marked completed)
const isJoinEnabled = (dateStr, timeStr) => {
  try {
    if (!dateStr || !timeStr) return false;
    const classDateTime = new Date(`${dateStr}T${timeStr}:00`);
    const now = new Date();
    const diffMs = classDateTime - now;
    // PLATFORM RULE: link active 10 minutes BEFORE class start, expires 20 minutes AFTER start
    return diffMs <= 10 * 60 * 1000 && diffMs > -20 * 60 * 1000;
  } catch { return false; }
};

import ImpersonationBanner from '../components/ImpersonationBanner';
import TeacherEarningsDashboard from '../components/TeacherEarningsDashboard';
import TeacherReportCardManager from '../components/TeacherReportCardManager';
import RightUtilityRail from '../components/RightUtilityRail';
import { getTopicImage, getTopicCategory } from '../utils/topicImages';
import apiClient from '../api';

// Local Feedback tab: lists a teacher's pending feedback items inline
function FeedbackTab({ onOpen, refreshKey }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get('/feedback/pending');
        if (!ignore) setItems(res.data.pending || []);
      } catch { if (!ignore) setItems([]); }
      finally { if (!ignore) setLoading(false); }
    })();
    return () => { ignore = true; };
  }, [refreshKey]);
  if (loading) return <div className="text-center py-12 text-slate-500 text-sm">Loading pending feedback…</div>;
  if (items.length === 0) return (
    <div className="text-center py-16 text-slate-500 font-semibold" data-testid="fb-tab-empty">
      <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No pending feedback</p>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="fb-tab-list">
      {items.map(it => (
        <button
          key={it.class_id}
          onClick={() => onOpen(it.class_id)}
          className="w-full text-left glass-card rounded-xl p-4 hover:shadow-md transition flex items-center gap-3"
          data-testid={`fb-tab-item-${it.class_id}`}
        >
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <ClipboardList className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-slate-900 truncate">{it.student_name || 'Student'}</p>
            <p className="text-xs text-slate-500">{it.date} · {it.time || '—'} · {it.course || 'Class'}{it.topic_name ? ` · ${it.topic_name}` : ''}</p>
          </div>
          {it.has_draft && <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">Draft</span>}
          <span className="text-xs font-bold text-indigo-600 hover:text-indigo-800">Give feedback →</span>
        </button>
      ))}
    </div>
  );
}

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const token = localStorage.getItem('token');
  const api = useMemo(() => axios.create({ baseURL: `${API}/api`, withCredentials: true, headers: { Authorization: `Bearer ${token}` } }), [token]);

  // Navigation
  const [activeSection, setActiveSection] = useState('classes');
  const [mobileSidebar, setMobileSidebar] = useState(false);
  // Class Feedback module
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackClassId, setFeedbackClassId] = useState(null);
  const [pendingRefreshKey, setPendingRefreshKey] = useState(0);
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);
  const [availabilityChooserOpen, setAvailabilityChooserOpen] = useState(false);
  useEffect(() => {
    let cancel = false;
    const run = async () => {
      try {
        const res = await apiClient.get('/feedback/pending');
        if (!cancel) setPendingFeedbackCount((res.data.pending || []).length);
      } catch { /* silent */ }
    };
    run();
    const t = setInterval(run, 60000);
    return () => { cancel = true; clearInterval(t); };
  }, [pendingRefreshKey]);
  const [aiCoPilotOpen, setAiCoPilotOpen] = useState(false);
  const [topicPicker, setTopicPicker] = useState({ open: false, cls: null, options: [], loading: false });
  const [showProfile, setShowProfile] = useState(false);
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);

  // Dashboard tabs
  const [classTab, setClassTab] = useState('classes');

  // Data
  const [upcomingClasses, setUpcomingClasses] = useState({ paid_classes: [], demo_classes: [] });
  const [students, setStudents] = useState([]);
  // Students split by activity status for THIS teacher.
  // Active list drives every dropdown (Schedule/Curriculum/Reports/Assignments/Messages)
  // so inactive students no longer clutter workflows. Full list is shown in the
  // Students tab where inactive rows are rendered in a separate section below.
  const activeStudents = React.useMemo(
    () => (students || []).filter(s => s.is_active_for_teacher !== false),
    [students]
  );
  const inactiveStudents = React.useMemo(
    () => (students || []).filter(s => s.is_active_for_teacher === false),
    [students]
  );
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [profile, setProfile] = useState(null);

  // Earnings
  const [earningsMonth, setEarningsMonth] = useState('2026-03');
  const [earnings, setEarnings] = useState(null);

  // Class Credits from Enrollment System
  const [studentCreditsMap, setStudentCreditsMap] = useState({});
  const [sendingReminder, setSendingReminder] = useState('');

  // Profile editing
  const [editingSkills, setEditingSkills] = useState(false);
  const [skillInput, setSkillInput] = useState('');
  const [editingBio, setEditingBio] = useState(false);
  const [bioInput, setBioInput] = useState('');
  const [editingVertical, setEditingVertical] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);

  // Curriculum
  const [curricula, setCurricula] = useState([]);
  const [currSelectedStudent, setCurrSelectedStudent] = useState('');
  const [newCurrOpen, setNewCurrOpen] = useState(false);
  const [newCurr, setNewCurr] = useState({ student_id: '', course_name: 'Coding 6-12 Years', category: 'Scratch' });
  const [editingClass, setEditingClass] = useState(null);
  const [classEditData, setClassEditData] = useState({ title: '', status: '', feedback: '' });

  // Class Management (Student Progress)
  const [progressSubView, setProgressSubView] = useState(null); // null = grid, 'class-progress', 'curriculum-view', 'report-card', 'learning-links'
  const [classEntries, setClassEntries] = useState([]);
  const [cmStudent, setCmStudent] = useState('');
  const [cmCurrTopics, setCmCurrTopics] = useState([]);
  const [cmNewEntry, setCmNewEntry] = useState({ topic_name: '', date: '', time: '', source: 'custom', status: 'incomplete', curriculum_id: '' });
  const [cmAddOpen, setCmAddOpen] = useState(false);
  const [cmCompletedFilter, setCmCompletedFilter] = useState('all'); // 'all' | 'current' | 'previous'
  const [cmEditEntry, setCmEditEntry] = useState(null);
  const [cmFilter, setCmFilter] = useState('all'); // 'all', 'incomplete', 'completed', 'carried_forward'
  const [cmSearch, setCmSearch] = useState('');
  const [cmTab, setCmTab] = useState('upcoming'); // 'upcoming', 'completed', 'other'

  // Assignments
  const [assignments, setAssignments] = useState([]);
  const [asgnDialogOpen, setAsgnDialogOpen] = useState(false);
  const [newAsgn, setNewAsgn] = useState({ student_id: '', title: '', description: '', due_date: '', attachments: [] });

  // Resource editing (Presentation & Class Notes)
  const [resourceEditOpen, setResourceEditOpen] = useState(false);
  const [resourceTarget, setResourceTarget] = useState(null);
  const [resourceForm, setResourceForm] = useState({ presentation_url: '', class_notes_url: '' });
  const [newAttachment, setNewAttachment] = useState({ name: '', url: '' });
  const [gradeDialogOpen, setGradeDialogOpen] = useState(false);
  const [gradingAsgn, setGradingAsgn] = useState(null);
  const [gradeData, setGradeData] = useState({ grade: '', feedback: '' });

  // References
  const [references, setReferences] = useState([]);
  const [refDialogOpen, setRefDialogOpen] = useState(false);
  const [newRef, setNewRef] = useState({ student_id: '', title: '', url: '', type: 'video', description: '' });

  // Reports
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportRemarks, setReportRemarks] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportViewOpen, setReportViewOpen] = useState(false);
  const [teacherReports, setTeacherReports] = useState([]);

  // Notifications
  const [notifPrefs, setNotifPrefs] = useState({ email_enabled: true, whatsapp_enabled: false, phone: '' });

  // Class Feedback
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);

  // Discussion Threads
  const [discussionThreads, setDiscussionThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [newThreadData, setNewThreadData] = useState({ student_id: '', subject: '', message: '' });
  const [threadMessage, setThreadMessage] = useState('');

  // Report Cards
  const [reportCards, setReportCards] = useState([]);
  const [reportCardOpen, setReportCardOpen] = useState(false);
  const [reportCardData, setReportCardData] = useState({
    student_id: '', teacher_feedback: '', areas_of_improvement: '',
    student_grade: '', concepts_covered: [], overall_progress: '', final_grade: '',
    class_rows: [],
  });
  const [generatingRC, setGeneratingRC] = useState(false);
  const [viewingRC, setViewingRC] = useState(null);
  const [newConcept, setNewConcept] = useState({ title: '', points: '' });

  // Curriculum Template Mapping
  const [mapCurrOpen, setMapCurrOpen] = useState(false);
  const [mapVerticals, setMapVerticals] = useState([]);
  const [mapSelectedVertical, setMapSelectedVertical] = useState(null);
  const [mapModules, setMapModules] = useState([]);
  const [mapSelectedModule, setMapSelectedModule] = useState(null);
  const [mapModuleDetails, setMapModuleDetails] = useState(null);
  const [mapStudentId, setMapStudentId] = useState('');
  const [mapSelectedTopics, setMapSelectedTopics] = useState([]);
  const [customCurrMode, setCustomCurrMode] = useState(false);
  const [customCourseName, setCustomCourseName] = useState('');
  const [customTopics, setCustomTopics] = useState('');

  // Learning Links
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkStudentId, setLinkStudentId] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkDescription, setLinkDescription] = useState('');
  const [studentLinks, setStudentLinks] = useState([]);
  const [viewLinksStudentId, setViewLinksStudentId] = useState('');

  const fetchMapVerticals = async () => {
    try { const res = await api.get('/teacher/curriculum-verticals'); setMapVerticals(res.data); } catch (err) { {}; }
  };
  const fetchMapModuleDetails = async (moduleId) => {
    try { const res = await api.get(`/teacher/curriculum-module/${moduleId}`); setMapModuleDetails(res.data); setMapSelectedTopics(res.data.topics.map(t => t.id)); } catch (err) { {}; }
  };
  const handleMapCurriculum = async () => {
    if (customCurrMode) {
      if (!mapStudentId || !customCourseName.trim() || !customTopics.trim()) { toast.error('Fill all fields'); return; }
      try {
        const topicsList = customTopics.split('\n').map(t => t.trim()).filter(Boolean);
        await api.post('/teacher/map-custom-curriculum', { student_id: mapStudentId, course_name: customCourseName, topics: topicsList });
        toast.success('Custom curriculum mapped!');
        setMapCurrOpen(false); setCustomCurrMode(false); setCustomCourseName(''); setCustomTopics(''); setMapStudentId('');
        if (currSelectedStudent) handleCurrStudentSelect(currSelectedStudent);
      } catch (err) {
        if (err.response?.status === 409) { toast.warning(err.response?.data?.detail || 'This custom curriculum is already assigned.'); }
        else { toast.error('Failed to map'); }
      }
      return;
    }
    if (!mapStudentId || !mapSelectedModule) { toast.error('Select student and module'); return; }
    try {
      await api.post('/teacher/map-curriculum', { student_id: mapStudentId, module_id: mapSelectedModule, topic_ids: mapSelectedTopics });
      toast.success('Curriculum mapped to student!');
      setMapCurrOpen(false); setMapSelectedVertical(null); setMapModules([]); setMapSelectedModule(null); setMapModuleDetails(null); setMapStudentId(''); setMapSelectedTopics([]);
      if (currSelectedStudent) handleCurrStudentSelect(currSelectedStudent);
    } catch (err) {
      if (err.response?.status === 409) { toast.warning(err.response?.data?.detail || 'This module is already assigned. Each module can only be assigned once.'); }
      else { toast.error('Failed to map'); }
    }
  };

  const handleCreateLink = async () => {
    if (!linkStudentId || !linkTitle.trim() || !linkUrl.trim()) { toast.error('Fill student, title, and URL'); return; }
    try {
      await api.post('/teacher/learning-links', { student_id: linkStudentId, title: linkTitle, url: linkUrl, description: linkDescription });
      toast.success('Link shared with student!');
      setLinkDialogOpen(false); setLinkTitle(''); setLinkUrl(''); setLinkDescription(''); setLinkStudentId('');
      if (viewLinksStudentId) fetchStudentLinks(viewLinksStudentId);
    } catch { toast.error('Failed to create link'); }
  };
  const fetchStudentLinks = async (sid) => {
    try { const res = await api.get(`/teacher/learning-links/${sid}`); setStudentLinks(res.data || []); } catch { setStudentLinks([]); }
  };
  const handleDeleteLink = async (linkId) => {
    try { await api.delete(`/teacher/learning-links/${linkId}`); toast.success('Link deleted'); if (viewLinksStudentId) fetchStudentLinks(viewLinksStudentId); } catch { toast.error('Failed'); }
  };

  // Leave Requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  // === Class Management Functions ===
  const fetchClassEntries = async (sid) => {
    try { const res = await api.get(`/teacher/class-entries/${sid}`); setClassEntries(res.data || []); } catch { setClassEntries([]); }
  };
  const fetchCmCurrTopics = async (sid) => {
    try { const res = await api.get(`/teacher/student-curricula/${sid}`); setCmCurrTopics(res.data || []); } catch { setCmCurrTopics([]); }
  };
  const handleCmStudentChange = (sid) => {
    setCmStudent(sid);
    if (sid) { fetchClassEntries(sid); fetchCmCurrTopics(sid); }
    else { setClassEntries([]); setCmCurrTopics([]); }
  };
  const handleCmCreate = async () => {
    if (!cmStudent || !cmNewEntry.topic_name.trim()) { toast.error('Select student and enter topic'); return; }
    try {
      await api.post('/teacher/class-entries', { student_id: cmStudent, ...cmNewEntry });
      toast.success('Class entry created!');
      setCmAddOpen(false); setCmNewEntry({ topic_name: '', date: '', time: '', source: 'custom', status: 'incomplete', curriculum_id: '' });
      fetchClassEntries(cmStudent);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
  };
  const handleCmUpdate = async (entryId, data) => {
    try {
      await api.put(`/teacher/class-entries/${entryId}`, data);
      toast.success('Updated!');
      setCmEditEntry(null);
      fetchClassEntries(cmStudent);
    } catch { toast.error('Update failed'); }
  };
  const handleCmDelete = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api.delete(`/teacher/class-entries/${entryId}`);
      toast.success('Deleted');
      fetchClassEntries(cmStudent);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Delete failed';
      toast.error(msg);
    }
  };
  const handleDeleteCurriculum = async (studentId, curriculumId, curriculumName) => {
    if (!window.confirm(`Remove curriculum "${curriculumName}" from this student?\n\nThis will delete the mapping and linked pending classes.`)) return;
    try {
      const res = await api.delete(`/teacher/curriculum/${curriculumId}`);
      toast.success(res.data.message || 'Curriculum deleted successfully');
      if (studentId) handleCurrStudentSelect(studentId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove curriculum');
    }
  };
  const handleCmAssignTopic = (topic) => {
    setCmNewEntry({ topic_name: topic.topic_title, date: '', time: '', source: 'curriculum', status: 'incomplete', curriculum_id: topic.curriculum_id });
    setCmAddOpen(true);
  };

  // Report Issue
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueData, setIssueData] = useState({ subject: '', description: '' });
  const [myIssues, setMyIssues] = useState([]);
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [leaveReason, setLeaveReason] = useState('');
  const [selectedLeaveSlots, setSelectedLeaveSlots] = useState([]);
  const [leaveMarkingMode, setLeaveMarkingMode] = useState(false);
  const [submittingLeave, setSubmittingLeave] = useState(false);

  // Weekly Calendar
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d;
  });
  const [availabilityType, setAvailabilityType] = useState('long_term');
  const [teacherAvailability, setTeacherAvailability] = useState([]);
  const [savingAvail, setSavingAvail] = useState(false);
  const [availMarkingMode, setAvailMarkingMode] = useState(false);
  const [selectedAvailSlots, setSelectedAvailSlots] = useState(new Set());
  const [upcomingViewMode, setUpcomingViewMode] = useState('week');

  // Schedule
  const [teacherSchedule, setTeacherSchedule] = useState({ upcoming_classes: [], recurring_schedules: [] });
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ reason: '', proposed_date: '', proposed_time: '' });
  const [studentRatings, setStudentRatings] = useState({ ratings: [], averages: {}, total_ratings: 0 });
  const [feedbackViewOpen, setFeedbackViewOpen] = useState(false);
  const [feedbackTarget, setFeedbackTarget] = useState(null);
  const [feedbackRatings, setFeedbackRatings] = useState({ focus_in_class: 0, project_completion: 0, concept_understanding: 0, independence: 0 });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [classFeedbacks, setClassFeedbacks] = useState({});
  const [viewingFeedback, setViewingFeedback] = useState(null);
  const FEEDBACK_QUESTIONS = [
    { key: 'focus_in_class', label: 'Focus in Class' },
    { key: 'project_completion', label: 'Project Completion' },
    { key: 'concept_understanding', label: 'Ability to Understand Concepts' },
    { key: 'independence', label: 'Ability to Create Project Independently' },
  ];

  const quote = QUOTES[Math.floor(Math.random() * QUOTES.length)];

  const fetchData = useCallback(async () => {
    try {
      const [studentsRes, upcomingRes, profileRes] = await Promise.all([
        api.get('/teachers/students'),
        api.get('/teacher/upcoming-classes'),
        api.get('/teacher/profile')
      ]);
      setStudents(studentsRes.data);
      setUpcomingClasses(upcomingRes.data);
      setProfile(profileRes.data);
      // Fetch class credits for all students
      try {
        const creditsRes = await api.get('/teacher/student-credits');
        const credMap = {};
        for (const c of creditsRes.data || []) { credMap[c.student_id] = c; }
        setStudentCreditsMap(credMap);
      } catch (err) {  }
    } catch (err) {  }
  }, [api]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (userData.role !== 'teacher') {
      const isImpersonating = localStorage.getItem('impersonating') === 'true';
      if (!isImpersonating) { navigate('/login'); return; }
    }
    setUser(userData);
    fetchData();
  }, [navigate, fetchData]);

  const fetchEarnings = useCallback(async (month) => {
    try { const res = await api.get(`/teacher/earnings?month=${month}`); setEarnings(res.data); } catch (err) {  }
  }, [api]);

  const fetchMyIssues = useCallback(async () => {
    try { const res = await api.get('/teacher/my-issues'); setMyIssues(res.data); } catch (err) {  }
  }, [api]);

  useEffect(() => { if (activeSection === 'earnings') { fetchEarnings(earningsMonth); fetchMyIssues(); } }, [activeSection, earningsMonth, fetchEarnings, fetchMyIssues]);

  const fetchAssignments = useCallback(async () => { try { const res = await api.get('/teacher/assignments'); setAssignments(res.data); } catch (err) {  } }, [api]);
  const fetchReferences = useCallback(async () => { try { const res = await api.get('/teacher/references'); setReferences(res.data); } catch (err) {  } }, [api]);
  const fetchTeacherReports = useCallback(async () => { try { const res = await api.get('/teacher/reports'); setTeacherReports(res.data); } catch (err) {  } }, [api]);

  // Define these useCallback functions BEFORE the useEffect that uses them
  const fetchTeacherSchedule = useCallback(async () => {
    try {
      const res = await api.get('/teacher/schedule');
      setTeacherSchedule(res.data);
    } catch (err) {  }
    try {
      const rRes = await api.get('/teacher/student-ratings');
      setStudentRatings(rRes.data);
    } catch (err) {  }
  }, [api]);

  const fetchDiscussionThreads = useCallback(async () => {
    try { const res = await api.get('/teacher/discussion-threads'); setDiscussionThreads(res.data); } catch (err) {  }
  }, [api]);

  const fetchReportCards = useCallback(async () => {
    try { const res = await api.get('/teacher/report-cards'); setReportCards(res.data); } catch (err) {  }
  }, [api]);

  const fetchAvailability = useCallback(async () => {
    try { const res = await api.get('/teacher/availability'); setAvailSlots(res.data); } catch (err) {  }
    try { const res = await api.get('/teacher/demo-classes'); setDemoClasses(res.data); } catch (err) {  }
    try { const res = await api.get('/teacher/leave-requests'); setLeaveRequests(res.data); } catch (err) {  }
  }, [api]);

  const handleSendRenewalReminder = async (studentId) => {
    setSendingReminder(studentId);
    try {
      await api.post('/teacher/send-renewal-reminder', { student_id: studentId });
      toast.success('Renewal reminder sent to parent');
      // Refresh credits
      const creditsRes = await api.get('/teacher/student-credits');
      const credMap = {};
      for (const c of creditsRes.data || []) { credMap[c.student_id] = c; }
      setStudentCreditsMap(credMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
    setSendingReminder('');
  };

  useEffect(() => {
    if (activeSection === 'assignments') fetchAssignments();
    if (activeSection === 'reports') fetchTeacherReports();
    if (activeSection === 'calendar') { fetchTeacherSchedule(); fetchAvailability(); }
    if (activeSection === 'discussions') fetchDiscussionThreads();
    if (activeSection === 'report-card') fetchReportCards();
  }, [activeSection, fetchAssignments, fetchTeacherReports, fetchTeacherSchedule, fetchAvailability, fetchDiscussionThreads, fetchReportCards]);

  // Fetch report cards when opening report-card sub-view in Student Progress
  useEffect(() => {
    if (progressSubView === 'report-card') fetchReportCards();
  }, [progressSubView, fetchReportCards]);

  const handleRequestReschedule = async () => {
    if (!rescheduleData.proposed_date || !rescheduleData.proposed_time) { toast.error('Pick a date and time'); return; }
    try {
      await api.put(`/schedule/${rescheduleTarget.id}/reschedule`, {
        date: rescheduleData.proposed_date,
        time: rescheduleData.proposed_time,
      });
      toast.success('Class rescheduled — Zoom link updated and everyone notified');
      setRescheduleDialogOpen(false);
      setRescheduleData({ reason: '', proposed_date: '', proposed_time: '' });
      fetchTeacherSchedule();
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) toast.error(detail || 'That slot conflicts with another class');
      else toast.error(detail || 'Reschedule failed');
    }
  };

  const handleCancelClass = async (cls) => {
    if (!cls?.id) return;
    if (!window.confirm(`Cancel this class with ${cls.student_name} on ${cls.date} at ${cls.time}?\n\nThe Zoom meeting will be deleted and everyone linked will be emailed.`)) return;
    try {
      await api.delete(`/schedule/${cls.id}`);
      toast.success('Class cancelled — Zoom removed and notifications sent');
      fetchTeacherSchedule();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    try { const res = await api.get(`/teachers/students/${student.id}/details`); setStudentDetails(res.data); } catch { toast.error('Failed to load details'); }
  };

  const handleCurrStudentSelect = async (studentId) => {
    setCurrSelectedStudent(studentId);
    try { const res = await api.get(`/curriculum/${studentId}`); setCurricula(res.data); } catch (err) {  }
    // Fetch feedbacks for all curricula
    try {
      const currRes = await api.get(`/curriculum/${studentId}`);
      const fbMap = {};
      for (const curr of currRes.data) {
        try {
          const fbRes = await api.get(`/teacher/class-feedback/${curr.id}`);
          for (const fb of fbRes.data) { fbMap[`${curr.id}_${fb.class_number}`] = fb; }
        } catch (err) {  }
      }
      setClassFeedbacks(fbMap);
    } catch (err) {  }
  };

  const openFeedbackDialog = (curriculumId, cls) => {
    setFeedbackTarget({ curriculum_id: curriculumId, class_number: cls.class_number, class_title: cls.title });
    setFeedbackRatings({ focus_in_class: 0, project_completion: 0, concept_understanding: 0, independence: 0 });
    setFeedbackDialogOpen(true);
  };

  const submitFeedback = async () => {
    if (Object.values(feedbackRatings).some(v => v === 0)) { toast.error('Please rate all questions'); return; }
    setSubmittingFeedback(true);
    try {
      const res = await api.post('/teacher/class-feedback', { curriculum_id: feedbackTarget.curriculum_id, class_number: feedbackTarget.class_number, ratings: feedbackRatings });
      toast.success('Feedback submitted');
      setFeedbackDialogOpen(false);
      setClassFeedbacks(prev => ({ ...prev, [`${feedbackTarget.curriculum_id}_${feedbackTarget.class_number}`]: res.data }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    setSubmittingFeedback(false);
  };

  const viewFeedback = (curriculumId, classNumber) => {
    const fb = classFeedbacks[`${curriculumId}_${classNumber}`];
    if (fb) { setViewingFeedback(fb); setFeedbackViewOpen(true); }
  };

  const handleCreateCurriculum = async () => {
    if (!newCurr.student_id) { toast.error('Select a student'); return; }
    try {
      await api.post('/curriculum', newCurr);
      toast.success('Curriculum created');
      setNewCurrOpen(false);
      setNewCurr({ student_id: '', course_name: 'Coding 6-12 Years', category: 'Scratch' });
      if (currSelectedStudent) handleCurrStudentSelect(currSelectedStudent);
    } catch { toast.error('Failed'); }
  };

  const handleSaveClassEdit = async (curriculumId, classNumber) => {
    try { await api.patch(`/curriculum/${curriculumId}/class/${classNumber}`, classEditData); toast.success('Updated'); setEditingClass(null); handleCurrStudentSelect(currSelectedStudent); } catch { toast.error('Failed'); }
  };

  const handleSendAssignment = async () => {
    if (!newAsgn.student_id || !newAsgn.title) { toast.error('Fill required fields'); return; }
    try { await api.post('/teacher/assignments', newAsgn); toast.success('Sent!'); setAsgnDialogOpen(false); setNewAsgn({ student_id: '', title: '', description: '', due_date: '', attachments: [] }); fetchAssignments(); } catch { toast.error('Failed'); }
  };

  const handleAddAttachment = () => {
    if (!newAttachment.name || !newAttachment.url) return;
    setNewAsgn({ ...newAsgn, attachments: [...newAsgn.attachments, { ...newAttachment }] });
    setNewAttachment({ name: '', url: '' });
  };

  const handleGradeAssignment = async () => {
    if (!gradeData.grade) { toast.error('Select grade'); return; }
    try { await api.post('/teacher/assignments/grade', { assignment_id: gradingAsgn.id, ...gradeData }); toast.success('Graded!'); setGradeDialogOpen(false); fetchAssignments(); } catch { toast.error('Failed'); }
  };

  const handleAddReference = async () => {
    if (!newRef.student_id || !newRef.title || !newRef.url) { toast.error('Fill required'); return; }
    try { await api.post('/teacher/references', newRef); toast.success('Added!'); setRefDialogOpen(false); setNewRef({ student_id: '', title: '', url: '', type: 'video', description: '' }); fetchReferences(); } catch { toast.error('Failed'); }
  };

  const handleGenerateReport = async (studentId) => {
    setGeneratingReport(true);
    try {
      const res = await api.post(`/teacher/student-report/${studentId}`, { remarks: reportRemarks });
      setCurrentReport(res.data); setReportDialogOpen(false); setReportViewOpen(true); setReportRemarks('');
      toast.success('Report generated & shared!'); fetchTeacherReports();
    } catch { toast.error('Failed'); }
    setGeneratingReport(false);
  };

  const handleUpdateProfile = async (updates) => {
    try { await api.put('/teacher/profile', updates); const res = await api.get('/teacher/profile'); setProfile(res.data); toast.success('Updated!'); } catch { toast.error('Failed'); }
  };

  const handleSaveResources = async () => {
    if (!resourceTarget) return;
    try {
      await api.put(`/curriculum-template/${resourceTarget.template_id}/topic/${resourceTarget.topic_id}/resources`, {
        presentation_url: resourceForm.presentation_url,
        class_notes_url: resourceForm.class_notes_url,
      });
      toast.success('Resources saved!');
      setResourceEditOpen(false);
      // Refresh upcoming classes to show updated links
      try { const res = await api.get('/teacher/upcoming-classes'); setUpcomingClasses(res.data); } catch {}
    } catch { toast.error('Failed to save resources'); }
  };

  const openResourceEdit = (cls) => {
    const resources = cls.resources || {};
    setResourceTarget({ template_id: resources.template_id || '', topic_id: resources.topic_id || '', topic_name: cls.topic_name || cls.title || 'Class' });
    setResourceForm({ presentation_url: resources.presentation_url || '', class_notes_url: resources.class_notes_url || '' });
    setResourceEditOpen(true);
  };

  // Curriculum-Engine wired topic picker
  const openTopicPicker = async (cls) => {
    if (!cls?.student_id) { toast.error('Missing student on class'); return; }
    setTopicPicker({ open: true, cls, options: [], loading: true });
    try {
      const res = await api.get(`/curriculum-engine/next-topic/${cls.student_id}`);
      const summary = await api.get(`/curriculum-engine/module-summary/${cls.student_id}`);
      const curr = summary.data || {};
      const next = res.data?.next_topic;
      // In flexible mode, offer any pending topic; in locked, offer only "next".
      let options = [];
      if (res.data?.progression_mode === 'flexible' && curr.has_curriculum) {
        // We need the topic list — call the student curriculum endpoint (existing).
        try {
          const stCur = await api.get(`/student/curriculum-details/${cls.student_id}`);
          const topics = (stCur.data?.classes || stCur.data?.topics || []).filter(t => t.status !== 'completed');
          options = topics.map(t => ({ id: t.id, title: t.title || t.name, recommended: next?.id === t.id }));
        } catch {
          options = next ? [{ id: next.id, title: next.title || next.name, recommended: true }] : [];
        }
      } else if (next) {
        options = [{ id: next.id, title: next.title || next.name, recommended: true }];
      }
      setTopicPicker({ open: true, cls, options, loading: false, mode: res.data?.progression_mode });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load topics');
      setTopicPicker({ open: false, cls: null, options: [], loading: false });
    }
  };

  const applyTopicPick = async (topic) => {
    const cls = topicPicker.cls;
    try {
      await api.post('/curriculum-engine/select-topic', { class_id: cls.id, topic_id: topic.id });
      toast.success(`Topic set: ${topic.title}`);
      setTopicPicker({ open: false, cls: null, options: [], loading: false });
      // refresh upcoming classes
      const res = await api.get('/teacher/upcoming-classes');
      setUpcomingClasses(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set topic');
    }
  };

  const fetchProfile = async () => {
    try { const res = await api.get('/teacher/profile'); setProfile(res.data); } catch (err) {  }
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only MP4, MOV, WEBM files allowed');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File must be under 100MB');
      return;
    }
    setVideoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/upload/video/teacher-profile', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setProfile(prev => ({ ...prev, video_url: res.data.video_url }));
      toast.success('Video uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setVideoUploading(false);
  };

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); };

  const formatINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

  // Discussion handlers
  const handleCreateThread = async () => {
    if (!newThreadData.student_id || !newThreadData.subject) { toast.error('Select student and topic'); return; }
    try {
      await api.post('/teacher/discussion-threads', newThreadData);
      toast.success('Thread created!');
      setNewThreadOpen(false);
      setNewThreadData({ student_id: '', subject: '', message: '' });
      fetchDiscussionThreads();
    } catch { toast.error('Failed to create thread'); }
  };
  const handleSendThreadMessage = async () => {
    if (!threadMessage.trim() || !selectedThread) return;
    try {
      await api.post(`/teacher/discussion-threads/${selectedThread.id}/messages`, { message: threadMessage });
      setThreadMessage('');
      const res = await api.get(`/discussion-threads/${selectedThread.id}`);
      setSelectedThread(res.data);
      fetchDiscussionThreads();
    } catch { toast.error('Failed to send'); }
  };

  // Earnings recalculation
  const handleRecalculateEarnings = async () => {
    try {
      await api.post('/teacher/recalculate-earnings');
      toast.success('Earnings recalculated from completed classes!');
      fetchEarnings(earningsMonth);
    } catch { toast.error('Failed to recalculate'); }
  };

  // Report Issue handlers — uses shared fetchMyIssues from above
  const handleReportIssue = async () => {
    if (!issueData.description.trim()) { toast.error('Please describe the issue'); return; }
    setSubmittingIssue(true);
    try {
      await api.post('/teacher/report-issue', { type: 'earnings', subject: issueData.subject, description: issueData.description, month: earningsMonth });
      toast.success('Issue reported successfully');
      setIssueDialogOpen(false);
      setIssueData({ subject: '', description: '' });
      fetchMyIssues();
    } catch { toast.error('Failed to report issue'); }
    setSubmittingIssue(false);
  };

  // Report Card handlers
  const handleGenerateReportCard = async () => {
    if (!reportCardData.student_id) { toast.error('Select a student'); return; }
    if (!reportCardData.teacher_feedback && !reportCardData.overall_progress) { toast.error('Add feedback or overall progress'); return; }
    setGeneratingRC(true);
    try {
      const res = await api.post('/teacher/report-card', reportCardData);
      toast.success('Report Card generated!');
      setReportCardOpen(false);
      setReportCardData({ student_id: '', teacher_feedback: '', areas_of_improvement: '', student_grade: '', concepts_covered: [], overall_progress: '', final_grade: '', class_rows: [] });
      setNewConcept({ title: '', points: '' });
      setViewingRC(res.data);
      fetchReportCards();
    } catch { toast.error('Failed to generate'); }
    setGeneratingRC(false);
  };

  // Calendar helpers
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(calendarWeekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  };
  const shiftWeek = (dir) => {
    setCalendarWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };
  const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const calendarHours = Array.from({length: 13}, (_, i) => i + 8); // 8 AM to 8 PM
  const calendarSlots = []; // 30-min slots for leave
  for (let h = 8; h <= 20; h++) {
    calendarSlots.push({ hour: h, min: 0, label: `${h > 12 ? h-12 : h === 0 ? 12 : h}:00 ${h >= 12 ? 'PM' : 'AM'}` });
    calendarSlots.push({ hour: h, min: 30, label: `${h > 12 ? h-12 : h === 0 ? 12 : h}:30 ${h >= 12 ? 'PM' : 'AM'}` });
  }

  // Availability handlers
  const [availSlots, setAvailSlots] = useState([]);
  const [demoClasses, setDemoClasses] = useState([]);

  const handleApplyLeave = async () => {
    if (selectedLeaveSlots.length === 0) { toast.error('Select at least one slot'); return; }
    if (!leaveReason.trim()) { toast.error('Please provide a reason'); return; }
    setSubmittingLeave(true);
    try {
      const slots = selectedLeaveSlots.map(key => {
        const [date, time] = key.split('|');
        return { date, start_time: time, end_time: `${String(parseInt(time.split(':')[0]) + 1).padStart(2, '0')}:00` };
      });
      await api.post('/teacher/leave-request', { slots, reason: leaveReason });
      toast.success('Leave request submitted!');
      setLeaveDialogOpen(false);
      setLeaveMarkingMode(false);
      setSelectedLeaveSlots([]);
      setLeaveReason('');
      fetchAvailability();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSubmittingLeave(false);
  };

  const toggleLeaveSlot = (key) => {
    setSelectedLeaveSlots(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const openCalendarInNewWindow = () => {
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/teacher/calendar`, '_blank', 'width=1400,height=900');
  };

  const handleDeleteAvailSlot = async (slotId) => {
    try { await api.delete(`/teacher/availability/${slotId}`); toast.success('Slot removed'); fetchAvailability(); } catch (err) {  }
  };

  const toggleAvailMarking = (type) => {
    if (availMarkingMode && availabilityType === type) {
      setAvailMarkingMode(false);
      setSelectedAvailSlots(new Set());
    } else {
      setAvailabilityType(type);
      setAvailMarkingMode(true);
      setSelectedAvailSlots(new Set());
    }
  };

  const toggleSlotSelection = (key) => {
    setSelectedAvailSlots(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const saveAvailabilitySlots = async () => {
    if (selectedAvailSlots.size === 0) { toast.error('Select at least one slot'); return; }
    setSavingAvail(true);
    const slots = Array.from(selectedAvailSlots).map(key => {
      const [dateOrDay, hour] = key.split('|');
      const startTime = `${hour}:00`;
      const endH = String(parseInt(hour) + 1).padStart(2, '0');
      const endTime = `${endH}:00`;
      if (availabilityType === 'long_term') {
        return { day_of_week: dateOrDay, start_time: startTime, end_time: endTime };
      } else {
        return { date: dateOrDay, start_time: startTime, end_time: endTime };
      }
    });
    try {
      await api.post('/teacher/availability', { type: availabilityType, slots });
      toast.success(`${slots.length} slot(s) saved as available!`);
      setAvailMarkingMode(false);
      setSelectedAvailSlots(new Set());
      fetchAvailability();
    } catch { toast.error('Failed to save'); }
    setSavingAvail(false);
  };

  const sidebarItems = [
    { id: 'classes', label: 'My Classes', icon: Home, color: 'text-blue-600 bg-blue-100' },
    { id: 'calendar', label: 'Calendar', icon: Calendar, color: 'text-violet-600 bg-violet-100' },
    { id: 'availability', label: 'Availability', icon: Clock, color: 'text-teal-600 bg-teal-100' },
    { id: 'schedule-requests', label: 'Schedule Requests', icon: CalendarClock, color: 'text-amber-600 bg-amber-100' },
    { id: 'students', label: 'Students', icon: Users, color: 'text-emerald-700 font-bold bg-emerald-100' },
    { id: 'student-progress', label: 'Student Progress', icon: BookOpen, color: 'text-cyan-600 bg-cyan-100' },
    { id: 'homework', label: 'Homework', icon: BookOpen, color: 'text-indigo-600 bg-indigo-100' },
    { id: 'coding-projects', label: 'Coding Projects', icon: CodeIcon, color: 'text-violet-600 bg-violet-100' },
    { id: 'quizzes', label: 'Quizzes', icon: Target, color: 'text-violet-600 bg-violet-100' },
    { id: 'report-cards-monthly', label: 'Reports', icon: FileText, color: 'text-purple-600 bg-purple-100' },
    { id: 'discussions', label: 'Messages', icon: MessageCircle, color: 'text-pink-600 bg-pink-100' },
    { id: 'earnings', label: 'My Earnings', icon: IndianRupee, color: 'text-green-600 bg-green-100' },
    { id: 'my-files', label: 'My Files', icon: Folder, color: 'text-emerald-600 bg-emerald-100' },
    { id: 'recordings', label: 'Recordings', icon: Video, color: 'text-teal-600 bg-teal-100' },
    { id: 'upskill', label: 'Upskill', icon: TrendingUp, color: 'text-orange-600 bg-orange-100' },
  ];

  const upskillSections = [
    {
      title: 'Artificial Intelligence',
      courses: [
        { name: 'Elements of AI', url: 'https://www.elementsofai.com/' },
        { name: 'IBM SkillsBuild - AI Catalog', url: 'https://skillsbuild.org/students/course-catalog/artificial-intelligence' },
        { name: 'IBM SkillsBuild - AI for Adults', url: 'https://skillsbuild.org/adult-learners/explore-learning/artificial-intelligence' },
        { name: 'Coursera - Intro to AI', url: 'https://www.coursera.org/learn/introduction-to-ai' },
        { name: 'Google AI Learning Hub', url: 'https://grow.google/ai/' },
        { name: 'Great Learning - Intro to AI', url: 'https://www.mygreatlearning.com/academy/learn-for-free/courses/introduction-to-artificial-intelligence' },
      ]
    },
    {
      title: 'Coding / Computer Science',
      courses: [
        { name: 'Google CS First', url: 'https://csfirst.withgoogle.com/' },
      ]
    },
    {
      title: 'Coding / Machine Learning',
      courses: [
        { name: 'Google ML Crash Course', url: 'https://developers.google.com/machine-learning/crash-course' },
        { name: 'Coursera - Machine Learning', url: 'https://www.coursera.org/courses?query=machine%20learning' },
      ]
    },
    {
      title: 'Coding / AI',
      courses: [
        { name: 'Google Cloud AI & ML Training', url: 'https://cloud.google.com/learn/training/machinelearning-ai' },
      ]
    },
    {
      title: 'Robotics & AI',
      courses: [
        { name: 'Great Learning - Robotics & AI', url: 'https://www.mygreatlearning.com/academy/learn-for-free/courses/robotics-and-ai' },
        { name: 'IBM AI Foundations (ISTE & IBM)', url: 'https://skillsbuild.org/students/course-catalog/artificial-intelligence/ai-foundations-powered-by-iste-and-ibm' },
      ]
    },
  ];

  if (!user) return null;

  const paidCount = upcomingClasses.paid_classes?.length || 0;
  const demoCount = upcomingClasses.demo_classes?.length || 0;

  const isImpersonating = localStorage.getItem('impersonating') === 'true';

  return (
    <div className="flex h-screen v2 v2-app" data-testid="teacher-dashboard">
      <OnboardingWizard
        open={showOnboardingWizard}
        onClose={() => setShowOnboardingWizard(false)}
        mode="teacher"
        user={user}
        onComplete={() => { /* Admin reviews — refresh just to pick up DB changes */ window.location.reload(); }}
      />
      {/* Mobile sidebar overlay */}
      {mobileSidebar && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileSidebar(false)} />}

      {/* LEFT SIDEBAR */}
      <aside className={`w-64 v2-sidebar flex-shrink-0 fixed inset-y-0 left-0 z-50 transition-transform duration-300 ${mobileSidebar ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static`}>
        <div className="flex items-center justify-between mb-4 px-2">
          <div className="flex items-center gap-2.5">
            <img src="/assets/logo.png" alt="CodeITT" className="h-11 w-auto object-contain rounded-lg" data-testid="codeitt-logo" />
            <div>
              <span className="text-base font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', 'Manrope', sans-serif" }}>CodeITT</span>
              <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600 -mt-0.5">Teacher Portal</p>
            </div>
          </div>
          <button onClick={() => setMobileSidebar(false)} className="lg:hidden p-1 text-slate-500 hover:text-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto pr-1">
          {sidebarItems.map(item => {
            const Icon = item.icon;
            const active = activeSection === item.id && !showProfile;
            return (
              <button key={item.id} onClick={() => {
                if (item.id === 'calendar') {
                  window.open(`${window.location.origin}/teacher/calendar`, '_blank', 'width=1500,height=900');
                  return;
                }
                setActiveSection(item.id); setShowProfile(false); setSelectedStudent(null);
                setMobileSidebar(false);
              }}
                className={`v2-nav-item w-full text-left ${active ? 'active' : ''}`}
                data-testid={`sidebar-${item.id}`}>
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* MAIN + RIGHT */}
      {/* MAIN AREA */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden lg:mr-80">
        {/* TOP BAR */}
        <header className="h-12 glass-topbar flex items-center px-4 sm:px-6 flex-shrink-0">
          <button onClick={() => setMobileSidebar(true)} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" data-testid="teacher-mobile-menu">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <button onClick={() => { setShowProfile(true); setActiveSection(''); }}
              className="flex items-center gap-2 hover:bg-slate-100 rounded-lg px-3 py-1.5 transition-colors" data-testid="teacher-profile-btn">
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center"><User className="w-3.5 h-3.5 text-blue-700 font-bold" /></div>
              <span className="text-sm font-bold text-slate-800">{user.name}</span>
            </button>
            <PendingFeedbackDropdown onOpen={(cid) => { setFeedbackClassId(cid); setFeedbackOpen(true); }} />
            <NotificationBell role="teacher" />
            <Button onClick={handleLogout} className="bg-red-500 hover:bg-red-600 text-white font-bold shadow-sm" size="sm" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /><span className="text-sm">Logout</span>
            </Button>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* CENTER CONTENT */}
          <main className="flex-1 overflow-y-auto admin-content">
            <AnnouncementsBanner />

            {/* === MY CLASSES (Dashboard Home) === */}
            {activeSection === 'classes' && !showProfile && (
              <div className="p-3 sm:p-6 w-full">
                {/* Welcome */}
                <div className="ds-page-hero" data-testid="teacher-page-hero">
                  <h1>Hi {user.name?.split(' ')[0]}</h1>
                  <p className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Time displayed in Asia/Calcutta IST (+05:30)</p>
                </div>

                {/* Teacher readiness checklist (shown until admin activates teacher) */}
                {!user?.is_active_teaching && (
                  <div className="nb-card p-5 bg-amber-50 border-amber-300 mb-5" data-testid="teacher-readiness-nudge">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <div className="flex-1">
                        <h3 className="font-bold text-slate-900 text-base">Get ready to go live</h3>
                        <ul className="text-sm text-slate-700 mt-2 space-y-1">
                          <li>• Complete your demo class</li>
                          <li>• Finish onboarding (bio + skills + intro video)</li>
                          <li>• Set up your weekly availability</li>
                        </ul>
                        <Button size="sm" className="nb-btn-primary mt-3" onClick={() => setShowOnboardingWizard(true)} data-testid="teacher-onboarding-start-btn">
                          Start onboarding
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-slate-500/10 rounded-xl p-5 mb-6 border border-black/10">
                  <p className="text-slate-600 font-semibold italic leading-relaxed">"{quote.text}"</p>
                  <p className="text-sm text-slate-500 font-semibold mt-2 text-right">— {quote.author}</p>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-200 mb-6">
                  {[
                    { id: 'classes', label: 'My Classes', count: `${paidCount} Paid, ${demoCount} Demo`, color: 'text-blue-600' },
                    { id: 'feedback', label: 'Class Feedback', count: `${pendingFeedbackCount} pending`, color: 'text-orange-500' },
                    { id: 'evaluation', label: 'Evaluation', count: '0 pending', color: 'text-orange-500' },
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setClassTab(tab.id)}
                      className={`px-6 py-3 text-sm font-medium relative ${classTab === tab.id ? 'text-teal-600' : 'text-slate-500 font-semibold hover:text-slate-700 font-semibold'}`}
                      data-testid={`tab-${tab.id}`}>
                      <span>{tab.label}</span>
                      <div className={`text-xs mt-0.5 ${tab.color}`}>{tab.count}</div>
                      {classTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />}
                    </button>
                  ))}
                </div>

                {/* Class Cards */}
                {classTab === 'classes' && (
                  <div className="space-y-4">
                    {/* Paid Classes - max 5, no completed */}
                    {(upcomingClasses.paid_classes || []).filter(c => c.status !== 'completed').slice(0, 5).map(cls => (
                      <div key={cls.id} className="glass-card rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-5 hover:shadow-md transition-shadow" data-testid={`paid-class-${cls.id}`}>
                        <div className="w-full sm:w-44 h-24 sm:h-32 rounded-xl flex-shrink-0 relative overflow-hidden group">
                          <img src={getTopicImage(cls.topic_name || cls.title, cls.course)} alt={cls.topic_name || cls.title || 'Class'} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-white/90 rounded text-[10px] text-slate-800 font-bold shadow-sm">{getTopicCategory(cls.topic_name || cls.title, cls.course)}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">Paid</span>
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded truncate">{cls.course || 'coding'}</span>
                          </div>
                          <h3 className="font-bold text-black text-base sm:text-lg truncate">{cls.title || cls.topic_name || 'Scheduled Class'}</h3>
                          {(cls.vertical || cls.module) && (
                            <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mt-0.5">
                              {[cls.vertical, cls.module].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(' · ')}
                            </p>
                          )}
                          {cls.topic_name && cls.topic_status !== 'pending' ? (
                            <p className="text-xs font-bold text-indigo-600 mt-0.5">Topic: {cls.topic_name}</p>
                          ) : (
                            <button
                              onClick={() => openTopicPicker(cls)}
                              className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase tracking-wider hover:bg-amber-200 transition-colors"
                              data-testid={`pick-topic-${cls.id}`}
                            >
                              <Plus className="w-3 h-3" /> Pick Today's Topic
                            </button>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center"><GraduationCap className="w-3.5 h-3.5 text-amber-600" /></div>
                            <p className="text-sm font-medium text-slate-700 font-semibold truncate">{cls.student_name || 'Student'}</p>
                          </div>
                          {/* Resource Links */}
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {cls.resources?.project_url && (
                              <a href={cls.resources.project_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-[10px] font-bold border border-blue-200 hover:bg-blue-100 transition-colors" data-testid={`project-link-${cls.id}`}>
                                <ExternalLink className="w-3 h-3" /> Project
                              </a>
                            )}
                            {cls.resources?.presentation_url ? (
                              <a href={cls.resources.presentation_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 text-violet-700 rounded-lg text-[10px] font-bold border border-violet-200 hover:bg-violet-100 transition-colors" data-testid={`pres-link-${cls.id}`}>
                                <MonitorPlay className="w-3 h-3" /> Slides
                              </a>
                            ) : cls.resources?.template_id && (
                              <button onClick={() => openResourceEdit(cls)}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-violet-50 text-violet-400 rounded-lg text-[10px] font-bold border border-dashed border-violet-300 hover:bg-violet-100 hover:text-violet-600 transition-colors" data-testid={`add-pres-${cls.id}`}>
                                <Plus className="w-3 h-3" /> Slides
                              </button>
                            )}
                            {cls.resources?.class_notes_url ? (
                              <a href={cls.resources.class_notes_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-[10px] font-bold border border-amber-200 hover:bg-amber-100 transition-colors" data-testid={`notes-link-${cls.id}`}>
                                <FileText className="w-3 h-3" /> Notes
                              </a>
                            ) : cls.resources?.template_id && (
                              <button onClick={() => openResourceEdit(cls)}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-400 rounded-lg text-[10px] font-bold border border-dashed border-amber-300 hover:bg-amber-100 hover:text-amber-600 transition-colors" data-testid={`add-notes-${cls.id}`}>
                                <Plus className="w-3 h-3" /> Notes
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0 flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                          <p className="text-sm text-slate-500 font-semibold">{cls.date}</p>
                          <p className="text-xl sm:text-2xl font-bold text-black">{cls.time || cls.start_time}</p>
                          {(() => {
                            const canJoin = isJoinEnabled(cls.date, cls.time || cls.start_time);
                            return canJoin ? (
                              <button onClick={async () => {
                                try {
                                  const res = await api.post(`/class/${cls.id}/join`);
                                  if (res.data.meeting_link) { window.open(res.data.meeting_link, '_blank'); toast.success('Joined — duration tracking started'); }
                                  else toast.info('Meeting link not configured');
                                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
                              }} className="inline-flex items-center justify-center mt-2 w-10 h-10 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 text-white shadow-[0_4px_6px_rgba(16,185,129,0.4),inset_0_1px_0_rgba(255,255,255,0.2)] hover:from-emerald-300 hover:to-emerald-500 hover:shadow-[0_6px_12px_rgba(16,185,129,0.5),inset_0_1px_0_rgba(255,255,255,0.3)] active:shadow-[0_1px_2px_rgba(16,185,129,0.4),inset_0_1px_3px_rgba(0,0,0,0.2)] active:translate-y-[1px] transition-all cursor-pointer" data-testid={`join-class-${cls.id}`}><Video className="w-4 h-4" /></button>
                            ) : (
                              <span className="inline-flex items-center justify-center mt-2 w-10 h-10 rounded-full bg-gradient-to-b from-slate-200 to-slate-300 text-slate-400 shadow-[0_2px_4px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.5)] cursor-not-allowed" title="Available 1 min before class" data-testid={`join-disabled-${cls.id}`}><Video className="w-4 h-4" /></span>
                            );
                          })()}
                          {/* End Class → opens the Feedback dialog */}
                          <button
                            onClick={async () => {
                              try { await api.post(`/feedback/end-class/${cls.id}`); } catch { /* soft-fail */ }
                              setFeedbackClassId(cls.id);
                              setFeedbackOpen(true);
                            }}
                            className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors"
                            title="End class and give feedback"
                            data-testid={`end-class-${cls.id}`}
                          >
                            <CheckCircle className="w-3 h-3" /> End Class
                          </button>
                          <div className="flex items-center gap-1.5 mt-2">
                            <button
                              onClick={() => { setRescheduleTarget(cls); setRescheduleData({ reason: '', proposed_date: cls.date || '', proposed_time: cls.time || '' }); setRescheduleDialogOpen(true); }}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                              title="Reschedule this class"
                              data-testid={`reschedule-class-${cls.id}`}
                            >Reschedule</button>
                            <button
                              onClick={() => handleCancelClass(cls)}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors"
                              title="Cancel this class"
                              data-testid={`cancel-class-${cls.id}`}
                            >Cancel</button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Demo Classes - max 5, no completed */}
                    {(upcomingClasses.demo_classes || []).filter(c => c.status !== 'completed').slice(0, 5).map(demo => (
                      <div key={demo.id} className="glass-card rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-5 hover:shadow-md transition-shadow" data-testid={`demo-class-${demo.id}`}>
                        <div className="w-full sm:w-44 h-24 sm:h-32 rounded-xl flex-shrink-0 relative overflow-hidden group">
                          <img src={getTopicImage(demo.course_interest, '', true)} alt={demo.name || 'Demo'} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                          <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-amber-100 rounded text-[10px] text-amber-800 font-bold shadow-sm">Demo</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded">Demo</span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-600 font-bold text-xs font-bold rounded truncate">{demo.course_interest || 'General'}</span>
                          </div>
                          <h3 className="font-bold text-black text-base sm:text-lg truncate">Demo - {demo.name}</h3>
                          <p className="text-sm text-slate-500 font-semibold mt-1 truncate">{demo.email} &middot; {demo.phone}</p>
                          <p className="text-xs text-slate-500 font-semibold mt-1">{demo.timezone}</p>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0 flex sm:flex-col items-center sm:items-end gap-2 sm:gap-0">
                          <p className="text-sm text-slate-500 font-semibold">{demo.preferred_date}</p>
                          <p className="text-xl sm:text-2xl font-bold text-black">{demo.preferred_time || 'TBD'}</p>
                          <span className={`inline-block mt-1 sm:mt-2 px-3 py-1 rounded-lg text-xs font-bold ${demo.status === 'new' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-600 font-bold'}`}>{demo.status}</span>
                        </div>
                      </div>
                    ))}

                    {paidCount === 0 && demoCount === 0 && (
                      <div className="text-center py-16 text-slate-500 font-semibold">
                        <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p className="text-lg">No upcoming classes</p>
                      </div>
                    )}
                  </div>
                )}
                {classTab === 'feedback' && (
                  <FeedbackTab onOpen={(cid) => { setFeedbackClassId(cid); setFeedbackOpen(true); }} refreshKey={pendingRefreshKey} />
                )}
                {classTab === 'evaluation' && (
                  <div className="text-center py-16 text-slate-500 font-semibold"><CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>No pending evaluations</p></div>
                )}
              </div>
            )}

            {/* === STUDENTS === */}
            {activeSection === 'students' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl">
                <h1 className="text-xl sm:text-2xl font-bold text-black mb-6">My Students</h1>
                {selectedStudent && studentDetails ? (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-teal-100 flex items-center justify-center"><GraduationCap className="w-7 h-7 text-teal-600" /></div>
                          <div><h2 className="text-xl font-bold text-slate-900">{selectedStudent.name}</h2><p className="text-sm text-slate-500 font-semibold">{selectedStudent.email}</p></div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                            <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-report-btn"><ClipboardList className="w-4 h-4 mr-2" /> Generate Report</Button></DialogTrigger>
                            <DialogContent className="bg-white">
                              <DialogHeader><DialogTitle>Generate Progress Report</DialogTitle><DialogDescription>Create a report for {selectedStudent.name} — shared with parent</DialogDescription></DialogHeader>
                              <div className="space-y-4 mt-4">
                                <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-400/30 text-sm text-emerald-700 font-bold"><Share2 className="w-4 h-4 inline mr-1" /> This report will be shared with the parent</div>
                                <div><Label>Remarks</Label><Textarea value={reportRemarks} onChange={e => setReportRemarks(e.target.value)} placeholder="Add remarks..." rows={3} data-testid="report-remarks" /></div>
                                <Button onClick={() => handleGenerateReport(selectedStudent.id)} disabled={generatingReport} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-generate-report">{generatingReport ? 'Generating...' : 'Generate & Share'}</Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedStudent(null)}><X className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="text-center p-4 bg-emerald-500/10 rounded-xl"><div className="text-3xl font-bold text-emerald-700 font-bold">{studentDetails.classes_completed}</div><div className="text-sm text-slate-500 font-semibold">Completed</div></div>
                        <div className="text-center p-4 bg-teal-500/10 rounded-xl"><div className="text-3xl font-bold text-teal-600">{studentDetails.classes_subscribed}</div><div className="text-sm text-slate-500 font-semibold">Subscribed</div></div>
                      </div>
                      <Progress value={(studentDetails.classes_completed / (studentDetails.classes_subscribed || 1)) * 100} className="h-3 mb-6" />
                      {(studentDetails.enrollments || []).map((e, i) => (
                        <div key={i} className="mb-3"><div className="flex justify-between text-sm mb-1"><span className="font-medium">{e.course_title}</span><span className="text-teal-600">{e.progress}%</span></div><Progress value={e.progress} className="h-2" /></div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                  {/* Active students */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-sm font-bold text-slate-700" data-testid="active-students-heading">
                        Active Students <span className="text-slate-400 font-medium">({activeStudents.length})</span>
                      </h2>
                    </div>
                    {activeStudents.length === 0 ? (
                      <p className="text-sm text-slate-400 py-4">No active students yet.</p>
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="active-students-grid">
                    {activeStudents.map(s => {
                      const cred = studentCreditsMap[s.id];
                      const hasCredits = cred && cred.total_purchased > 0;
                      const lowBalance = cred && cred.low_balance;
                      return (
                      <div key={s.id} className={`nb-card nb-card-hover p-4 cursor-pointer ${lowBalance ? 'border-red-500 bg-red-50' : ''}`} data-testid={`student-card-${s.id}`}>
                        <div className="flex items-center gap-3" onClick={() => handleStudentClick(s)}>
                          <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-amber-600" /></div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-black truncate">{s.name}</h3>
                            <p className="text-xs text-slate-500 font-semibold truncate">{s.email}</p>
                            {(s.active_verticals_for_teacher || []).length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {s.active_verticals_for_teacher.map(v => (
                                  <span key={v} className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5 font-bold" data-testid={`vertical-tag-${s.id}-${v}`}>{v}</span>
                                ))}
                              </div>
                            )}
                            {s.grade && <span className="text-[10px] text-slate-500 font-semibold">Grade {s.grade}</span>}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            {s.timezone && (
                              <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-400/30 rounded-lg px-2.5 py-1.5" data-testid={`tz-badge-${s.id}`}>
                                <Globe className="w-3.5 h-3.5 text-blue-600" />
                                <div className="text-right">
                                  <p className="text-xs font-bold text-blue-600 font-bold">{s.timezone}</p>
                                  <p className="text-[10px] text-blue-500 font-mono">{getCurrentTimeInTZ(s.timezone)}</p>
                                </div>
                              </div>
                            )}
                            <ChevronRight className="w-4 h-4 text-slate-500 font-semibold" />
                          </div>
                        </div>
                        {/* Class Credits Row */}
                        {hasCredits && (
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 text-xs">
                                <span className="font-bold text-emerald-700 text-base" data-testid={`teacher-student-progress-${s.id}`}>
                                  {cred.classes_completed}<span className="text-slate-400">/{cred.total_purchased}</span>
                                  <span className="text-slate-500 font-medium ml-1">classes</span>
                                </span>
                                <span className={`font-bold ${lowBalance ? 'text-red-600' : 'text-amber-700'}`}>{cred.classes_remaining} Left</span>
                              </div>
                              {lowBalance && (
                                <Button size="sm" onClick={(e) => { e.stopPropagation(); handleSendRenewalReminder(s.id); }}
                                  disabled={sendingReminder === s.id}
                                  className="bg-red-500 hover:bg-red-600 text-white text-[10px] font-bold h-7 px-2"
                                  data-testid={`reminder-btn-${s.id}`}>
                                  {sendingReminder === s.id ? 'Sending...' : 'Send Renewal Reminder'}
                                </Button>
                              )}
                            </div>
                            {lowBalance && (
                              <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600 font-bold" data-testid={`low-balance-${s.id}`}>
                                <AlertTriangle className="w-3.5 h-3.5" /> Only {cred.classes_remaining} class(es) left
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    </div>
                    )}
                  </div>

                  {/* Inactive students */}
                  {inactiveStudents.length > 0 && (
                    <div className="mt-8">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-sm font-bold text-slate-500" data-testid="inactive-students-heading">
                          Inactive Students <span className="text-slate-400 font-medium">({inactiveStudents.length})</span>
                        </h2>
                        <span className="text-[10px] text-slate-400 italic">Hidden from scheduling / assignment dropdowns</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-70" data-testid="inactive-students-grid">
                        {inactiveStudents.map(s => (
                          <div key={s.id} className="nb-card p-4 bg-slate-50 border-slate-200" data-testid={`inactive-student-card-${s.id}`}>
                            <div className="flex items-center gap-3" onClick={() => handleStudentClick(s)}>
                              <div className="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-slate-500" /></div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-slate-700 truncate">{s.name}</h3>
                                <p className="text-xs text-slate-400 font-semibold truncate">{s.email}</p>
                                {(s.inactive_verticals_for_teacher || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {s.inactive_verticals_for_teacher.map(iv => (
                                      <span key={iv.vertical} title={iv.reason} className="text-[10px] bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5 font-bold" data-testid={`inactive-vertical-tag-${s.id}-${iv.vertical}`}>
                                        {iv.vertical}{iv.reason ? ` · ${iv.reason}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className="text-[10px] bg-slate-200 text-slate-600 rounded-full px-2 py-0.5 font-bold">INACTIVE</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            )}

            {/* === EARNINGS === */}
            {activeSection === 'earnings' && !showProfile && (
              <div>
                <TeacherEarningsDashboard />

                <div className="px-3 sm:px-6 max-w-5xl mt-4">
                  {/* Report Issue Button + Dialog */}
                  <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-500/10" data-testid="report-issue-btn">
                        <AlertTriangle className="w-4 h-4 mr-2" /> Report an Issue
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white">
                      <DialogHeader>
                        <DialogTitle>Report Earnings Issue</DialogTitle>
                        <DialogDescription>Describe your concern about {MONTHS.find(m => m.value === earningsMonth)?.label || earningsMonth} earnings</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div><Label>Subject</Label><Input value={issueData.subject} onChange={e => setIssueData({...issueData, subject: e.target.value})} placeholder="e.g., Missing classes in February" data-testid="issue-subject" /></div>
                        <div><Label>Description</Label><Textarea value={issueData.description} onChange={e => setIssueData({...issueData, description: e.target.value})} placeholder="Explain what's wrong with your earnings..." rows={4} data-testid="issue-description" /></div>
                        <Button onClick={handleReportIssue} disabled={submittingIssue} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="submit-issue-btn">
                          {submittingIssue ? 'Submitting...' : 'Submit Issue'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* My Issues History */}
                  {myIssues.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-bold text-slate-700 font-semibold mb-3">Your Reported Issues</h3>
                      <div className="space-y-2">
                        {myIssues.map(issue => (
                          <div key={issue.id} className={`p-3 rounded-lg border ${issue.status === 'open' ? 'border-red-400/30 bg-red-500/10' : 'border-green-400/30 bg-green-500/10'}`} data-testid={`my-issue-${issue.id}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium text-sm text-slate-800 font-semibold">{issue.subject || 'Earnings Issue'}</span>
                                {issue.month && <span className="ml-2 text-xs text-slate-500 font-semibold">{issue.month}</span>}
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${issue.status === 'open' ? 'bg-red-200 text-red-700' : 'bg-green-200 text-green-700'}`}>{issue.status?.toUpperCase()}</span>
                            </div>
                            <p className="text-xs text-slate-500 font-semibold mt-1">{issue.description}</p>
                            <p className="text-[10px] text-slate-500 font-semibold mt-1">{new Date(issue.created_at).toLocaleString()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeSection === 'student-progress' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-5xl space-y-6">
                {/* Grid Buttons - show when no sub-view */}
                {!progressSubView && (
                  <>
                    <h1 className="text-2xl font-bold text-black">Student Progress</h1>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {[
                        { id: 'class-progress', label: 'Class Progress', icon: Layers, color: 'from-blue-500 to-indigo-600' },
                        { id: 'curriculum-view', label: 'Curriculum', icon: BookOpen, color: 'from-teal-500 to-emerald-600' },
                        { id: 'report-card', label: 'Report Card', icon: FileText, color: 'from-purple-500 to-violet-600' },
                        { id: 'learning-links', label: 'Learning Links', icon: Link2, color: 'from-amber-500 to-orange-600' },
                      ].map(btn => (
                        <button key={btn.id} onClick={() => setProgressSubView(btn.id)}
                          className={`nb-card nb-card-hover p-6 flex flex-col items-center justify-center gap-3 transition-all`}
                          data-testid={`progress-${btn.id}`}
                        >
                          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${btn.color} flex items-center justify-center`}>
                            <btn.icon className="w-7 h-7 text-slate-900" />
                          </div>
                          <span className="text-sm font-bold text-black">{btn.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* Back button for sub-views */}
                {progressSubView && (
                  <button onClick={() => { setProgressSubView(null); setCmStudent(''); setClassEntries([]); }} className="flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-800">
                    <ChevronLeft className="w-4 h-4" /> Back to Student Progress
                  </button>
                )}

                {/* === CLASS PROGRESS SUB-VIEW === */}
                {progressSubView === 'class-progress' && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <h2 className="text-xl font-bold text-black">Class Progress</h2>
                      <div className="flex items-center gap-2">
                        <Select value={cmStudent} onValueChange={handleCmStudentChange}>
                          <SelectTrigger className="w-48 border-2 border-gray-300 text-black font-bold text-sm" data-testid="cm-student-select"><SelectValue placeholder="Select Student" /></SelectTrigger>
                          <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                        {cmStudent && <Button onClick={() => setCmAddOpen(true)} className="bg-gray-50 border border-blue-400/40 text-blue-700 font-bold hover:bg-blue-50" size="sm" data-testid="cm-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Class</Button>}
                      </div>
                    </div>

                    {cmStudent && (
                      <>
                        {/* Curriculum Topics Dropdown */}
                        <div className="glass-card rounded-xl p-4 space-y-3">
                          <p className="text-sm font-bold text-black">Assign from Curriculum</p>
                          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                            {cmCurrTopics.filter(t => t.status === 'pending').map((t, i) => (
                              <button key={i} onClick={() => handleCmAssignTopic(t)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-300/50 bg-blue-50/50 text-blue-700 font-bold text-xs hover:bg-blue-100 hover:border-blue-400 transition-all" data-testid={`cm-assign-${i}`}>
                                <Plus className="w-3 h-3" /> {t.topic_title}
                                <span className="text-[10px] text-slate-500 ml-1">({t.course_name})</span>
                              </button>
                            ))}
                            {cmCurrTopics.filter(t => t.status === 'pending').length === 0 && <p className="text-xs text-slate-500 font-bold">No pending curriculum topics</p>}
                          </div>
                        </div>

                        {/* Filters & Search */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="relative flex-1 min-w-[200px]">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                            <input value={cmSearch} onChange={e => setCmSearch(e.target.value)} placeholder="Search topics..." className="w-full pl-9 pr-3 py-2 rounded-lg border-2 border-gray-300 text-sm font-bold text-black" data-testid="cm-search" />
                          </div>
                          <div className="flex gap-1">
                            {['all', 'incomplete', 'completed', 'carried_forward'].map(f => (
                              <button key={f} onClick={() => setCmFilter(f)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${cmFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                data-testid={`cm-filter-${f}`}>
                                {f === 'all' ? 'All' : f === 'carried_forward' ? 'Carried' : f.charAt(0).toUpperCase() + f.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tabs: Upcoming | Completed | Other */}
                        <div className="flex gap-1 border-b border-gray-200 pb-1">
                          {['upcoming', 'completed', 'other'].map(tab => (
                            <button key={tab} onClick={() => setCmTab(tab)}
                              className={`px-4 py-2 rounded-t-lg text-sm font-bold transition-all ${cmTab === tab ? 'bg-white border-2 border-b-0 border-gray-300 text-black' : 'text-gray-500 hover:text-black'}`}
                              data-testid={`cm-tab-${tab}`}>
                              {tab === 'upcoming' ? 'Upcoming Classes' : tab === 'completed' ? 'Completed Classes' : 'Other Classes'}
                              <span className="ml-1 text-xs text-slate-500">
                                ({classEntries.filter(e => {
                                  if (tab === 'upcoming') return e.status !== 'completed' && e.source === 'curriculum';
                                  if (tab === 'completed') return e.status === 'completed';
                                  return e.source === 'custom';
                                }).length})
                              </span>
                            </button>
                          ))}
                        </div>

                        {/* Class entries list */}
                        {cmTab === 'completed' && (
                          <div className="flex gap-1 mb-3" data-testid="completed-filter">
                            {[{id: 'all', label: 'All'}, {id: 'current', label: 'Current Curriculum'}, {id: 'previous', label: 'Previous Curriculum'}].map(f => (
                              <button key={f.id} onClick={() => setCmCompletedFilter(f.id)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${cmCompletedFilter === f.id ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'}`}
                                data-testid={`cf-${f.id}`}>
                                {f.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <div className="space-y-2">
                          {classEntries
                            .filter(e => {
                              if (cmTab === 'upcoming') return e.status !== 'completed' && e.source === 'curriculum';
                              if (cmTab === 'completed') {
                                const isCompleted = e.status === 'completed';
                                if (!isCompleted) return false;
                                if (cmCompletedFilter === 'current') return !e.previous_curriculum;
                                if (cmCompletedFilter === 'previous') return !!e.previous_curriculum;
                                return true;
                              }
                              return e.source === 'custom';
                            })
                            .filter(e => cmFilter === 'all' || e.status === cmFilter)
                            .filter(e => !cmSearch || e.topic_name.toLowerCase().includes(cmSearch.toLowerCase()))
                            .map(entry => (
                              <div key={entry.id} className={`glass-card rounded-xl p-4 flex items-center gap-4 border-l-4 ${entry.status === 'completed' ? 'border-l-emerald-500' : entry.status === 'carried_forward' ? 'border-l-orange-500' : 'border-l-yellow-500'}`} data-testid={`cm-entry-${entry.id}`}>
                                {cmEditEntry?.id === entry.id ? (
                                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-5 gap-2 items-center">
                                    <input value={cmEditEntry.topic_name} onChange={e => setCmEditEntry({...cmEditEntry, topic_name: e.target.value})} className="border-2 border-gray-300 rounded-lg px-2 py-1.5 text-sm font-bold text-black" />
                                    <input type="date" value={cmEditEntry.date} onChange={e => setCmEditEntry({...cmEditEntry, date: e.target.value})} className="border-2 border-gray-300 rounded-lg px-2 py-1.5 text-sm font-bold text-black" />
                                    <input type="time" value={cmEditEntry.time} onChange={e => setCmEditEntry({...cmEditEntry, time: e.target.value})} className="border-2 border-gray-300 rounded-lg px-2 py-1.5 text-sm font-bold text-black" />
                                    <select value={cmEditEntry.status} onChange={e => setCmEditEntry({...cmEditEntry, status: e.target.value})} className="border-2 border-gray-300 rounded-lg px-2 py-1.5 text-sm font-bold text-black">
                                      <option value="incomplete">Incomplete</option>
                                      <option value="completed">Completed</option>
                                      <option value="carried_forward">Carried Forward</option>
                                    </select>
                                    <div className="flex gap-1">
                                      <Button size="sm" onClick={() => handleCmUpdate(entry.id, cmEditEntry)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Save</Button>
                                      <Button size="sm" variant="outline" onClick={() => setCmEditEntry(null)} className="text-xs">Cancel</Button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-bold text-black text-sm truncate">{entry.topic_name}</p>
                                      <p className="text-xs text-slate-500 font-bold">
                                        {entry.student_name}
                                        {entry.source === 'curriculum' && <span className="text-blue-500 ml-1">[Curriculum]</span>}
                                        {entry.previous_curriculum && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">From Previous Curriculum{entry.previous_curriculum_name ? `: ${entry.previous_curriculum_name}` : ''}</span>}
                                      </p>
                                    </div>
                                    <div className="text-right text-xs space-y-0.5 flex-shrink-0">
                                      <p className="font-bold text-black">{entry.date ? new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}</p>
                                      <p className="text-slate-500 font-bold">{entry.time || 'No time'}</p>
                                    </div>
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold flex-shrink-0 ${entry.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : entry.status === 'carried_forward' ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                      {entry.status === 'carried_forward' ? 'Carried' : entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                    </span>
                                    <div className="flex gap-1 flex-shrink-0">
                                      <button onClick={() => setCmEditEntry({...entry})} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600" data-testid={`cm-edit-${entry.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                                      {entry.status !== 'completed' ? (
                                        <button onClick={() => handleCmDelete(entry.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" data-testid={`cm-del-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                      ) : (
                                        <button disabled className="p-1.5 rounded-lg text-slate-300 cursor-not-allowed" title="Completed classes are preserved" data-testid={`cm-del-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            ))
                          }
                          {classEntries.filter(e => {
                            if (cmTab === 'upcoming') return e.status !== 'completed' && e.source === 'curriculum';
                            if (cmTab === 'completed') return e.status === 'completed';
                            return e.source === 'custom';
                          }).length === 0 && (
                            <div className="glass-card rounded-xl p-8 text-center">
                              <p className="text-sm font-bold text-slate-500">{cmTab === 'upcoming' ? 'No upcoming classes. Assign topics from curriculum above.' : cmTab === 'completed' ? 'No completed classes yet.' : 'No custom classes. Click "Add Class" to create one.'}</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {!cmStudent && <div className="glass-card rounded-xl p-10 text-center"><p className="text-sm font-bold text-slate-500">Select a student to manage their class progress</p></div>}

                    {/* Add Class Dialog */}
                    <Dialog open={cmAddOpen} onOpenChange={setCmAddOpen}>
                      <DialogContent className="bg-white border border-blue-200 shadow-xl max-w-md">
                        <DialogHeader><DialogTitle className="text-black font-bold">Add Class Entry</DialogTitle></DialogHeader>
                        <div className="space-y-4 mt-3">
                          <div><Label className="text-black font-bold">Topic Name</Label><Input value={cmNewEntry.topic_name} onChange={e => setCmNewEntry({...cmNewEntry, topic_name: e.target.value})} placeholder="e.g. Variables & Data Types" className="border-2 border-gray-300 text-black font-bold" data-testid="cm-topic-input" /></div>
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-black font-bold">Date</Label><Input type="date" value={cmNewEntry.date} onChange={e => setCmNewEntry({...cmNewEntry, date: e.target.value})} className="border-2 border-gray-300 text-black font-bold" data-testid="cm-date-input" /></div>
                            <div><Label className="text-black font-bold">Time</Label><Input type="time" value={cmNewEntry.time} onChange={e => setCmNewEntry({...cmNewEntry, time: e.target.value})} className="border-2 border-gray-300 text-black font-bold" data-testid="cm-time-input" /></div>
                          </div>
                          <div><Label className="text-black font-bold">Type</Label>
                            <select value={cmNewEntry.source} onChange={e => setCmNewEntry({...cmNewEntry, source: e.target.value})} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-bold text-black" data-testid="cm-source-select">
                              <option value="curriculum">From Curriculum</option>
                              <option value="custom">Custom / Other</option>
                            </select>
                          </div>
                          <div><Label className="text-black font-bold">Status</Label>
                            <select value={cmNewEntry.status} onChange={e => setCmNewEntry({...cmNewEntry, status: e.target.value})} className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm font-bold text-black" data-testid="cm-status-select">
                              <option value="incomplete">Incomplete</option>
                              <option value="completed">Completed</option>
                              <option value="carried_forward">Carried Forward</option>
                            </select>
                          </div>
                          <Button onClick={handleCmCreate} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="cm-save-btn">Save Entry</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {/* === CURRICULUM VIEW (existing) === */}
                {progressSubView === 'curriculum-view' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-black">Curriculum Management</h2>
                      <div className="flex gap-2">
                        <Button onClick={() => { setMapCurrOpen(true); fetchMapVerticals(); }} className="bg-gray-50 border border-teal-400/40 text-teal-700 font-bold hover:bg-teal-50" data-testid="map-curriculum-btn"><BookOpen className="w-4 h-4 mr-2" /> Map from Template</Button>
                        <Dialog open={newCurrOpen} onOpenChange={setNewCurrOpen}>
                          <DialogTrigger asChild><Button className="bg-gray-50 border border-indigo-400/40 text-indigo-700 font-bold hover:bg-indigo-50" data-testid="add-curriculum-btn"><Plus className="w-4 h-4 mr-2" /> New Curriculum</Button></DialogTrigger>
                          <DialogContent className="bg-white border border-gray-200 shadow-xl">
                            <DialogHeader><DialogTitle className="text-black font-bold">Create Curriculum</DialogTitle></DialogHeader>
                            <div className="space-y-4 mt-4">
                              <div><Label className="text-black font-bold">Student</Label><Select value={newCurr.student_id} onValueChange={v => setNewCurr({ ...newCurr, student_id: v })}><SelectTrigger className="border-2 border-gray-300 text-black"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
                              <div><Label className="text-black font-bold">Course</Label><Select value={newCurr.course_name} onValueChange={v => setNewCurr({ ...newCurr, course_name: v, category: COURSE_CATALOG[v]?.[0] || '' })}><SelectTrigger className="border-2 border-gray-300 text-black"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(COURSE_CATALOG).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                              <div><Label className="text-black font-bold">Subject</Label><Select value={newCurr.category} onValueChange={v => setNewCurr({ ...newCurr, category: v })}><SelectTrigger className="border-2 border-gray-300 text-black"><SelectValue /></SelectTrigger><SelectContent>{(COURSE_CATALOG[newCurr.course_name] || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                              <Button onClick={handleCreateCurriculum} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold">Create</Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                {/* Map Curriculum from Template Dialog */}
                <Dialog open={mapCurrOpen} onOpenChange={setMapCurrOpen}>
                  <DialogContent className="bg-white max-w-xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Map Curriculum to Student</DialogTitle><DialogDescription>Select a template or create a custom curriculum</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-3">
                      {/* Mode toggle */}
                      <div className="flex bg-slate-100 rounded-lg p-0.5">
                        <button onClick={() => setCustomCurrMode(false)} className={`flex-1 px-3 py-2 rounded-md text-sm font-bold transition-all ${!customCurrMode ? 'bg-white text-teal-700 shadow' : 'text-slate-600 font-semibold'}`}>From Template</button>
                        <button onClick={() => setCustomCurrMode(true)} className={`flex-1 px-3 py-2 rounded-md text-sm font-bold transition-all ${customCurrMode ? 'bg-white text-orange-700 shadow' : 'text-slate-600 font-semibold'}`}>Custom Curriculum</button>
                      </div>
                      <div><Label>Student</Label>
                        <Select value={mapStudentId} onValueChange={v => setMapStudentId(v)}>
                          <SelectTrigger data-testid="map-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                          <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {customCurrMode ? (
                        <>
                          <div><Label>Course Name</Label><Input value={customCourseName} onChange={e => setCustomCourseName(e.target.value)} placeholder="e.g. Advanced Python, SAT Math Prep" data-testid="custom-course-name" /></div>
                          <div><Label>Topics (one per line)</Label><Textarea value={customTopics} onChange={e => setCustomTopics(e.target.value)} placeholder={"Topic 1\nTopic 2\nTopic 3"} rows={6} data-testid="custom-topics" /></div>
                          <Button onClick={handleMapCurriculum} disabled={!mapStudentId || !customCourseName.trim() || !customTopics.trim()} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="map-custom-btn">Map Custom Curriculum</Button>
                        </>
                      ) : (
                        <>
                      <div><Label>Vertical</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
                          {mapVerticals.map(v => (
                            <button key={v.vertical} onClick={() => { setMapSelectedVertical(v.vertical); setMapModules(v.modules); setMapSelectedModule(null); setMapModuleDetails(null); }}
                              className={`p-3 rounded-lg border text-left text-sm font-medium transition-all ${mapSelectedVertical === v.vertical ? 'border-teal-500 bg-teal-500/10 text-teal-700' : 'border-slate-200 hover:border-teal-300'}`}>
                              {v.vertical}
                              <span className="block text-xs text-slate-500 font-semibold">{v.modules.length} modules</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {mapSelectedVertical && (
                        <div><Label>Module</Label>
                          <Select value={mapSelectedModule || ''} onValueChange={v => { setMapSelectedModule(v); fetchMapModuleDetails(v); }}>
                            <SelectTrigger><SelectValue placeholder="Select module" /></SelectTrigger>
                            <SelectContent>{mapModules.map(m => <SelectItem key={m.id} value={m.id}>{m.module_name} ({m.topic_count} topics)</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                      {mapModuleDetails && (
                        <div><Label>Topics (uncheck to skip)</Label>
                          <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1 mt-1">
                            {mapModuleDetails.topics.map((t, i) => (
                              <label key={t.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-500/10 text-sm cursor-pointer">
                                <input type="checkbox" checked={mapSelectedTopics.includes(t.id)} onChange={e => { setMapSelectedTopics(prev => e.target.checked ? [...prev, t.id] : prev.filter(id => id !== t.id)); }} className="rounded" />
                                <span className="font-mono text-xs text-slate-500 font-semibold">{i+1}.</span>
                                <span className="flex-1">{t.name}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${t.proficiency === 'Beginner' ? 'bg-green-100 text-green-700' : t.proficiency === 'Intermediate' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{t.proficiency}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button onClick={handleMapCurriculum} disabled={!mapStudentId || !mapSelectedModule} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="map-template-btn">Map Curriculum</Button>
                        </>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
                <div className="glass-card rounded-xl p-4">
                  <Select value={currSelectedStudent} onValueChange={handleCurrStudentSelect}>
                    <SelectTrigger data-testid="curriculum-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                    <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {curricula.map(curr => (
                  <div key={curr.id} className="glass-card rounded-xl overflow-hidden" data-testid={`curriculum-${curr.id}`}>
                    <div className="bg-gradient-to-r from-teal-600 to-cyan-600 px-6 py-4 flex items-center justify-between">
                      <div><h3 className="text-lg font-bold text-black">{curr.course_name}</h3><p className="text-indigo-200 text-sm">{curr.category} &middot; {curr.student_name}</p></div>
                      <div className="flex items-center gap-3">
                        <div className="text-center"><div className="text-2xl font-bold text-black">{curr.classes.filter(c => c.status === 'completed').length}/{curr.classes.length}</div><div className="text-xs text-indigo-200">Done</div></div>
                        <button onClick={() => handleDeleteCurriculum(currSelectedStudent, curr.id, curr.course_name || curr.module_name)} className="p-2 rounded-lg bg-white/20 hover:bg-red-500/80 text-white transition-colors" title="Remove curriculum (completed classes preserved)" data-testid={`remove-curr-${curr.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {curr.classes.map(cls => (
                        <div key={cls.class_number} className={`px-3 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-4 ${cls.status === 'completed' ? 'bg-green-500/10/50' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${cls.status === 'completed' ? 'bg-green-500/100 text-white' : cls.title ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-200 text-slate-400 font-semibold'}`}>{cls.class_number}</div>
                          {editingClass === `${curr.id}-${cls.class_number}` ? (
                            <div className="flex-1 flex flex-wrap items-center gap-2">
                              <Input value={classEditData.title} onChange={e => setClassEditData({ ...classEditData, title: e.target.value })} placeholder="Topic / Game" className="flex-1 min-w-[100px]" />
                              <Input type="date" value={classEditData.date || ''} onChange={e => setClassEditData({ ...classEditData, date: e.target.value })} className="w-32 sm:w-36" />
                              <Select value={classEditData.status} onValueChange={v => setClassEditData({ ...classEditData, status: v })}><SelectTrigger className="w-28 sm:w-32"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="in_progress">In Progress</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select>
                              <Input value={classEditData.feedback} onChange={e => setClassEditData({ ...classEditData, feedback: e.target.value })} placeholder="Feedback" className="flex-1 min-w-[100px]" />
                              <Button size="sm" className="bg-green-600" onClick={() => handleSaveClassEdit(curr.id, cls.class_number)}><Check className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingClass(null)}><X className="w-4 h-4" /></Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex-1 min-w-0"><span className={`font-medium ${cls.title ? 'text-slate-900' : 'text-gray-300 italic'}`}>{cls.title || `Class ${cls.class_number}`}</span>{cls.date && <span className="ml-2 text-xs text-slate-500 font-semibold">{cls.date}{cls.time ? ` ${cls.time}` : ''}</span>}{cls.feedback && <p className="text-xs text-slate-500 font-semibold mt-0.5">{cls.feedback}</p>}</div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls.status === 'completed' ? 'bg-green-100 text-green-700' : cls.status === 'in_progress' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-gray-100 text-slate-500 font-semibold'}`}>{cls.status === 'completed' ? 'Done' : cls.status === 'in_progress' ? 'In Progress' : 'Pending'}</span>
                              {cls.status === 'completed' && (
                                classFeedbacks[`${curr.id}_${cls.class_number}`] ? (
                                  <button onClick={() => viewFeedback(curr.id, cls.class_number)} className="px-3 py-1 bg-teal-100 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-200 transition-colors flex items-center gap-1" data-testid={`view-fb-${curr.id}-${cls.class_number}`}><Eye className="w-3 h-3" /> Report</button>
                                ) : (
                                  <button onClick={() => openFeedbackDialog(curr.id, cls)} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200 transition-colors flex items-center gap-1" data-testid={`give-fb-${curr.id}-${cls.class_number}`}><Star className="w-3 h-3" /> Feedback</button>
                                )
                              )}
                              <button onClick={() => { setEditingClass(`${curr.id}-${cls.class_number}`); setClassEditData({ title: cls.title, status: cls.status, feedback: cls.feedback, date: cls.date || '', time: cls.time || '' }); }} className="text-slate-500 font-semibold hover:text-teal-600" data-testid={`edit-class-${curr.id}-${cls.class_number}`}><Settings className="w-4 h-4" /></button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                  </div>
                )}

                {/* === LEARNING LINKS SUB-VIEW === */}
                {progressSubView === 'learning-links' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-black flex items-center gap-2"><Link2 className="w-5 h-5 text-orange-600" /> Learning Links</h2>
                      <Button onClick={() => setLinkDialogOpen(true)} className="bg-gray-50 border border-orange-400/40 text-orange-700 font-bold hover:bg-orange-50" data-testid="add-link-btn"><Plus className="w-4 h-4 mr-2" /> Share Link</Button>
                    </div>
                    <div className="glass-card rounded-xl p-4">
                      <Label className="text-sm font-bold text-black">View links for student</Label>
                      <Select value={viewLinksStudentId} onValueChange={v => { setViewLinksStudentId(v); fetchStudentLinks(v); }}>
                        <SelectTrigger className="w-48 border-2 border-gray-300 text-black font-bold text-sm mt-2" data-testid="links-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                        <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {viewLinksStudentId && (
                      studentLinks.length === 0 ? (
                        <div className="glass-card rounded-xl p-8 text-center"><p className="text-sm font-bold text-slate-500">No links shared with this student yet</p></div>
                      ) : (
                        <div className="space-y-2">
                          {studentLinks.map(link => (
                            <div key={link.id} className="glass-card rounded-xl p-4 flex items-center gap-3" data-testid={`link-${link.id}`}>
                              <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0"><Link2 className="w-4 h-4 text-orange-600" /></div>
                              <div className="flex-1 min-w-0">
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="font-bold text-sm text-black hover:text-orange-700">{link.title}</a>
                                {link.description && <p className="text-xs text-slate-500 font-bold truncate">{link.description}</p>}
                                <p className="text-xs text-slate-500 font-bold truncate">{link.url}</p>
                              </div>
                              <button onClick={() => handleDeleteLink(link.id)} className="text-slate-500 hover:text-red-500 p-1" data-testid={`delete-link-${link.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                        </div>
                      )
                    )}
                    {!viewLinksStudentId && <div className="glass-card rounded-xl p-10 text-center"><p className="text-sm font-bold text-slate-500">Select a student to view their learning links</p></div>}
                    <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
                      <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                        <DialogHeader><DialogTitle className="text-black font-bold">Share Learning Link</DialogTitle><DialogDescription className="text-gray-600">Share a resource link with a student</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-3">
                          <div><Label className="text-black font-bold">Student</Label>
                            <Select value={linkStudentId} onValueChange={v => setLinkStudentId(v)}>
                              <SelectTrigger className="border-2 border-gray-300 text-black font-bold" data-testid="link-student-select"><SelectValue placeholder="Select student" /></SelectTrigger>
                              <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label className="text-black font-bold">Title</Label><Input value={linkTitle} onChange={e => setLinkTitle(e.target.value)} placeholder="e.g. Python Practice Problems" className="border-2 border-gray-300 text-black font-bold" data-testid="link-title" /></div>
                          <div><Label className="text-black font-bold">URL</Label><Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..." className="border-2 border-gray-300 text-black font-bold" data-testid="link-url" /></div>
                          <div><Label className="text-black font-bold">Description (optional)</Label><Textarea value={linkDescription} onChange={e => setLinkDescription(e.target.value)} placeholder="Brief note about this resource" rows={2} className="border-2 border-gray-300 text-black font-bold" data-testid="link-description" /></div>
                          <Button onClick={handleCreateLink} disabled={!linkStudentId || !linkTitle.trim() || !linkUrl.trim()} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold" data-testid="submit-link-btn">Share Link</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}

                {/* === REPORT CARD SUB-VIEW === */}
                {progressSubView === 'report-card' && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-bold text-black">Report Cards</h2>
                      <Button onClick={() => setReportCardOpen(true)} className="bg-gray-50 border border-purple-400/40 text-purple-700 font-bold hover:bg-purple-50" data-testid="sp-new-rc-btn"><Plus className="w-4 h-4 mr-2" /> New Report Card</Button>
                    </div>
                    <Dialog open={reportCardOpen} onOpenChange={setReportCardOpen}>
                      <DialogContent className="bg-white border border-indigo-200 shadow-xl max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader><DialogTitle className="text-black font-bold text-lg">Generate Student Progress Report</DialogTitle><DialogDescription className="text-gray-600">Fill in the report card matching the CodeITT template</DialogDescription></DialogHeader>
                        <div className="space-y-4 mt-2">
                          {/* Student Selection */}
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-black font-bold text-xs">Student *</Label>
                              <Select value={reportCardData.student_id} onValueChange={v => setReportCardData(p => ({...p, student_id: v}))}>
                                <SelectTrigger data-testid="sp-rc-student-select" className="border-2 border-gray-300 text-black mt-1"><SelectValue placeholder="Select student" /></SelectTrigger>
                                <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                            <div><Label className="text-black font-bold text-xs">Student Grade/Level</Label>
                              <Input value={reportCardData.student_grade} onChange={e => setReportCardData(p => ({...p, student_grade: e.target.value}))} placeholder="e.g. Beginner, Grade 3" className="border-2 border-gray-300 text-black mt-1" data-testid="rc-grade" />
                            </div>
                          </div>

                          {/* Class Rows (auto-populated + editable) */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-black font-bold text-xs">Classes & Feedback</Label>
                              <Button variant="ghost" size="sm" className="text-blue-600 text-xs h-7" onClick={() => setReportCardData(p => ({...p, class_rows: [...p.class_rows, { class_name: '', date: '', feedback: '' }]}))}>
                                <Plus className="w-3 h-3 mr-1" /> Add Row
                              </Button>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {reportCardData.class_rows.map((row, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                                  <Input value={row.class_name} onChange={e => { const rows = [...reportCardData.class_rows]; rows[idx].class_name = e.target.value; setReportCardData(p => ({...p, class_rows: rows})); }} placeholder="Class name" className="col-span-4 text-xs h-8 border-gray-300" />
                                  <Input type="date" value={row.date} onChange={e => { const rows = [...reportCardData.class_rows]; rows[idx].date = e.target.value; setReportCardData(p => ({...p, class_rows: rows})); }} className="col-span-3 text-xs h-8 border-gray-300" />
                                  <Input value={row.feedback} onChange={e => { const rows = [...reportCardData.class_rows]; rows[idx].feedback = e.target.value; setReportCardData(p => ({...p, class_rows: rows})); }} placeholder="Feedback" className="col-span-4 text-xs h-8 border-gray-300" />
                                  <button onClick={() => { const rows = reportCardData.class_rows.filter((_, i) => i !== idx); setReportCardData(p => ({...p, class_rows: rows})); }} className="col-span-1 text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                                </div>
                              ))}
                              {reportCardData.class_rows.length === 0 && <p className="text-xs text-slate-400 text-center py-2">Classes will be auto-populated from completed sessions, or add manually</p>}
                            </div>
                          </div>

                          {/* Concepts Covered */}
                          <div>
                            <Label className="text-black font-bold text-xs mb-2 block">Concepts Covered</Label>
                            <div className="space-y-1.5 mb-2">
                              {reportCardData.concepts_covered.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200">
                                  <span className="text-xs font-bold text-slate-800 flex-1">{typeof c === 'string' ? c : c.title}{c.points ? ` — ${c.points}` : ''}</span>
                                  <button onClick={() => setReportCardData(p => ({...p, concepts_covered: p.concepts_covered.filter((_, idx) => idx !== i)}))} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-1.5">
                              <Input value={newConcept.title} onChange={e => setNewConcept(p => ({...p, title: e.target.value}))} placeholder="Concept title" className="text-xs h-8 border-gray-300 flex-1" data-testid="rc-concept-title" />
                              <Input value={newConcept.points} onChange={e => setNewConcept(p => ({...p, points: e.target.value}))} placeholder="Key points" className="text-xs h-8 border-gray-300 flex-1" />
                              <Button size="sm" className="h-8 px-2 bg-indigo-600 text-white" onClick={() => { if (newConcept.title.trim()) { setReportCardData(p => ({...p, concepts_covered: [...p.concepts_covered, { ...newConcept }]})); setNewConcept({ title: '', points: '' }); } }}><Plus className="w-3 h-3" /></Button>
                            </div>
                          </div>

                          {/* Overall Progress */}
                          <div><Label className="text-black font-bold text-xs">Overall Progress</Label>
                            <Textarea value={reportCardData.overall_progress} onChange={e => setReportCardData(p => ({...p, overall_progress: e.target.value}))} placeholder="Overall progress summary..." rows={2} className="border-2 border-gray-300 text-black mt-1" data-testid="rc-overall" />
                          </div>

                          {/* Teacher Feedback & Areas of Improvement */}
                          <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-black font-bold text-xs">Teacher Feedback</Label>
                              <Textarea value={reportCardData.teacher_feedback} onChange={e => setReportCardData(p => ({...p, teacher_feedback: e.target.value}))} placeholder="Strengths, observations..." rows={2} data-testid="sp-rc-feedback" className="border-2 border-gray-300 text-black mt-1" />
                            </div>
                            <div><Label className="text-black font-bold text-xs">Areas of Improvement</Label>
                              <Textarea value={reportCardData.areas_of_improvement} onChange={e => setReportCardData(p => ({...p, areas_of_improvement: e.target.value}))} placeholder="Focus areas..." rows={2} data-testid="sp-rc-improvement" className="border-2 border-gray-300 text-black mt-1" />
                            </div>
                          </div>

                          {/* Final Grade */}
                          <div><Label className="text-black font-bold text-xs">Final Evaluation Grade</Label>
                            <Select value={reportCardData.final_grade} onValueChange={v => setReportCardData(p => ({...p, final_grade: v}))}>
                              <SelectTrigger className="border-2 border-gray-300 text-black mt-1 w-32" data-testid="rc-final-grade"><SelectValue placeholder="Grade" /></SelectTrigger>
                              <SelectContent>
                                {['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>

                          <Button onClick={handleGenerateReportCard} disabled={generatingRC} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold" data-testid="sp-generate-rc-btn">
                            {generatingRC ? 'Generating...' : 'Generate Report Card'}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    {viewingRC && (
                      <div className="glass-card rounded-xl overflow-hidden shadow-sm" data-testid="sp-report-card-view">
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                          <div className="flex items-center justify-between">
                            <div><h2 className="text-xl font-bold text-slate-900">Monthly Report for {viewingRC.month_label}</h2><p className="text-indigo-200 text-sm">CodeITT Academy</p></div>
                            <div className="text-right"><p className="text-slate-900 font-bold">{viewingRC.student_name}</p><p className="text-indigo-200 text-xs">{new Date(viewingRC.generated_at).toLocaleDateString()}</p></div>
                          </div>
                        </div>
                        <div className="p-6 space-y-5">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-400/30"><div className="text-3xl font-bold text-emerald-700">{viewingRC.total_classes_completed}</div><div className="text-xs text-slate-500 font-bold mt-1">Total Classes</div></div>
                            <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-400/30"><div className="text-3xl font-bold text-blue-600">{viewingRC.classes_this_month}</div><div className="text-xs text-slate-500 font-bold mt-1">This Month</div></div>
                            <div className="text-center p-4 bg-purple-500/10 rounded-xl border border-purple-400/30"><div className="text-3xl font-bold text-purple-600">{viewingRC.projects_completed?.length || 0}</div><div className="text-xs text-slate-500 font-bold mt-1">Projects</div></div>
                          </div>
                          {(viewingRC.classes_this_month_detail?.length > 0 || viewingRC.classes_this_month > 0) && (
                            <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200/50">
                              <p className="text-sm font-bold text-blue-900 mb-3">Classes Completed in {viewingRC.month_label}</p>
                              {viewingRC.classes_this_month_detail?.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead><tr className="border-b border-blue-200/50"><th className="text-left py-2 px-3 font-bold text-blue-700">#</th><th className="text-left py-2 px-3 font-bold text-blue-700">Date</th><th className="text-left py-2 px-3 font-bold text-blue-700">Time</th><th className="text-left py-2 px-3 font-bold text-blue-700">Course</th><th className="text-left py-2 px-3 font-bold text-blue-700">Topic</th></tr></thead>
                                    <tbody>{viewingRC.classes_this_month_detail.map((cls, idx) => (
                                      <tr key={`${cls.date}-${cls.time}-${idx}`} className="border-b border-blue-100/50 last:border-0"><td className="py-2 px-3 font-bold text-black">{idx + 1}</td><td className="py-2 px-3 font-bold text-black">{cls.date ? new Date(cls.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td><td className="py-2 px-3 font-bold text-black">{cls.time || '-'}</td><td className="py-2 px-3 font-bold text-black">{cls.course_name || '-'}</td><td className="py-2 px-3 font-bold text-black">{cls.topic_name || '-'}</td></tr>
                                    ))}</tbody>
                                  </table>
                                </div>
                              ) : (<p className="text-sm text-slate-600 font-bold">{viewingRC.classes_this_month} class(es) completed this month</p>)}
                            </div>
                          )}
                          {viewingRC.curriculum_names?.length > 0 && (<div><p className="text-sm font-bold text-slate-700 mb-2">Curriculum</p><div className="flex flex-wrap gap-2">{viewingRC.curriculum_names.map((c, i) => <span key={c} className="px-3 py-1 bg-indigo-500/10 text-indigo-700 rounded-full text-xs font-bold border border-indigo-200">{c}</span>)}</div></div>)}
                          <div className="p-4 bg-teal-500/10 rounded-xl border border-teal-200"><p className="text-sm font-bold text-teal-900 mb-1">Teacher Feedback</p><p className="text-slate-700 font-bold text-sm">{viewingRC.teacher_feedback}</p></div>
                          {viewingRC.areas_of_improvement && (<div className="p-4 bg-amber-500/10 rounded-xl border border-amber-400/30"><p className="text-sm font-bold text-amber-900 mb-1">Areas of Improvement</p><p className="text-slate-700 font-bold text-sm">{viewingRC.areas_of_improvement}</p></div>)}
                          <Button variant="outline" size="sm" onClick={() => setViewingRC(null)} className="flex-1 font-bold">Close</Button>
                          {viewingRC.emailed ? (
                            <Button size="sm" disabled className="bg-slate-100 text-slate-400"><Mail className="w-4 h-4 mr-1" /> Emailed</Button>
                          ) : (
                            <Button size="sm" onClick={async () => {
                              try { const res = await api.post(`/teacher/report-card/${viewingRC.id}/email`); toast.success(res.data.message); setViewingRC({...viewingRC, emailed: true}); fetchReportCards(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                            }} className="bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="email-rc-btn"><Mail className="w-4 h-4 mr-1" /> Email to Parent</Button>
                          )}
                        </div>
                      </div>
                    )}
                    {!viewingRC && reportCards.length > 0 && (
                      <div className="space-y-3">
                        {reportCards.map(rc => (
                          <button key={rc.id} onClick={() => setViewingRC(rc)} className="w-full glass-card rounded-xl p-4 text-left hover:border-purple-300 hover:shadow-md transition-all" data-testid={`sp-rc-${rc.id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><Award className="w-5 h-5 text-purple-600" /></div>
                                <div><p className="font-bold text-black">{rc.student_name}</p><p className="text-xs text-slate-500 font-bold">Monthly Report for {rc.month_label} &middot; {rc.total_classes_completed} classes</p></div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    {!viewingRC && reportCards.length === 0 && (
                      <div className="glass-card rounded-xl p-10 text-center"><Award className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-sm font-bold text-slate-500">No report cards generated yet</p><p className="text-xs text-slate-500 mt-1">Generate a report card for one of your students</p></div>
                    )}
                  </div>
                )}

              </div>
            )}

            {/* FEEDBACK SUBMIT DIALOG */}
            <Dialog open={feedbackDialogOpen} onOpenChange={setFeedbackDialogOpen}>
              <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                <DialogHeader><DialogTitle>Class Feedback</DialogTitle><DialogDescription>{feedbackTarget?.class_title || `Class ${feedbackTarget?.class_number}`}</DialogDescription></DialogHeader>
                <div className="space-y-5 mt-4">
                  {FEEDBACK_QUESTIONS.map(q => (
                    <div key={q.key}>
                      <label className="text-sm font-medium text-slate-700 font-semibold mb-2 block">{q.label}</label>
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button key={star} onClick={() => setFeedbackRatings(prev => ({ ...prev, [q.key]: star }))}
                            className="p-1 transition-transform hover:scale-110" data-testid={`star-${q.key}-${star}`}>
                            <Star className={`w-7 h-7 ${star <= feedbackRatings[q.key] ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                          </button>
                        ))}
                        <span className="ml-2 text-sm text-slate-500 font-semibold self-center">{feedbackRatings[q.key]}/5</span>
                      </div>
                    </div>
                  ))}
                  <Button onClick={submitFeedback} disabled={submittingFeedback} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="submit-feedback-btn">
                    {submittingFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* FEEDBACK VIEW DIALOG */}
            <Dialog open={feedbackViewOpen} onOpenChange={setFeedbackViewOpen}>
              <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                <DialogHeader><DialogTitle>Class Report</DialogTitle><DialogDescription>{viewingFeedback?.class_title || `Class ${viewingFeedback?.class_number}`} &middot; {viewingFeedback?.student_name}</DialogDescription></DialogHeader>
                {viewingFeedback && (
                  <div className="space-y-4 mt-4">
                    <div className="flex items-center justify-between bg-teal-500/10 rounded-xl p-4 border border-teal-200">
                      <span className="font-bold text-black">Average Rating</span>
                      <div className="flex items-center gap-1">
                        <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                        <span className="text-xl font-bold text-teal-700">{viewingFeedback.average_rating}/5</span>
                      </div>
                    </div>
                    {FEEDBACK_QUESTIONS.map(q => (
                      <div key={q.key} className="flex items-center justify-between py-2 border-b border-black/10">
                        <span className="text-sm text-slate-700 font-semibold">{q.label}</span>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= viewingFeedback.ratings[q.key] ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
                          ))}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 font-semibold text-center">{viewingFeedback.course_name} &middot; {viewingFeedback.category} &middot; {new Date(viewingFeedback.created_at).toLocaleDateString()}</p>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* === ASSIGNMENTS === */}
            {activeSection === 'homework' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-7xl">
                <TeacherHomeworkSection />
              </div>
            )}

            {activeSection === 'coding-projects' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-7xl">
                <TeacherCodingReview />
              </div>
            )}

            {activeSection === 'messages' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-5xl">
                <ChatPanel currentUser={user} />
              </div>
            )}

            {activeSection === 'assignments' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold text-black">Assignments</h1>
                  <Dialog open={asgnDialogOpen} onOpenChange={setAsgnDialogOpen}>
                    <DialogTrigger asChild><Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-assignment-btn"><Send className="w-4 h-4 mr-2" /> Send Assignment</Button></DialogTrigger>
                    <DialogContent className="bg-white border border-gray-200 shadow-xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader><DialogTitle>Send Assignment</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div><Label>Student</Label><Select value={newAsgn.student_id} onValueChange={v => setNewAsgn({ ...newAsgn, student_id: v })}><SelectTrigger data-testid="asgn-student-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
                        <div><Label>Title</Label><Input value={newAsgn.title} onChange={e => setNewAsgn({ ...newAsgn, title: e.target.value })} data-testid="asgn-title-input" /></div>
                        <div><Label>Description</Label><Textarea value={newAsgn.description} onChange={e => setNewAsgn({ ...newAsgn, description: e.target.value })} data-testid="asgn-desc-input" /></div>
                        <div><Label>Due Date</Label><Input type="date" value={newAsgn.due_date} onChange={e => setNewAsgn({ ...newAsgn, due_date: e.target.value })} /></div>
                        <div className="border rounded-lg p-3">
                          <Label className="mb-2 block">Attachments</Label>
                          {newAsgn.attachments.map((a, i) => (<div key={i} className="flex items-center gap-2 mb-1 text-sm bg-slate-500/10 p-1.5 rounded"><Paperclip className="w-3 h-3" /><span className="flex-1 truncate">{a.name}</span><button onClick={() => setNewAsgn({ ...newAsgn, attachments: newAsgn.attachments.filter((_, j) => j !== i) })}><X className="w-3 h-3 text-red-500" /></button></div>))}
                          <div className="flex gap-2 mt-2"><Input placeholder="Name" value={newAttachment.name} onChange={e => setNewAttachment({ ...newAttachment, name: e.target.value })} className="flex-1" /><Input placeholder="URL" value={newAttachment.url} onChange={e => setNewAttachment({ ...newAttachment, url: e.target.value })} className="flex-1" /><Button type="button" variant="outline" size="sm" onClick={handleAddAttachment}>Add</Button></div>
                        </div>
                        <Button onClick={handleSendAssignment} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="asgn-submit-btn">Send</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-white rounded-xl p-4 border text-center"><div className="text-2xl font-bold">{assignments.length}</div><div className="text-xs text-slate-500 font-semibold">Total</div></div>
                  <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-400/30 text-center"><div className="text-2xl font-bold text-amber-700">{assignments.filter(a => a.status === 'pending').length}</div><div className="text-xs text-amber-600">Pending</div></div>
                  <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-400/30 text-center"><div className="text-2xl font-bold text-blue-600 font-bold">{assignments.filter(a => a.status === 'submitted').length}</div><div className="text-xs text-blue-600">Submitted</div></div>
                  <div className="bg-green-500/10 rounded-xl p-4 border border-green-400/30 text-center"><div className="text-2xl font-bold text-green-700">{assignments.filter(a => a.status === 'graded').length}</div><div className="text-xs text-green-600">Graded</div></div>
                </div>
                <Dialog open={gradeDialogOpen} onOpenChange={setGradeDialogOpen}>
                  <DialogContent className="bg-white"><DialogHeader><DialogTitle>Grade Assignment</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div><Label>Grade</Label><Select value={gradeData.grade} onValueChange={v => setGradeData({ ...gradeData, grade: v })}><SelectTrigger data-testid="grade-select"><SelectValue placeholder="Grade" /></SelectTrigger><SelectContent>{['A+','A','B+','B','C','D','F'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Feedback</Label><Textarea value={gradeData.feedback} onChange={e => setGradeData({ ...gradeData, feedback: e.target.value })} data-testid="grade-feedback" /></div>
                      <Button onClick={handleGradeAssignment} className="w-full bg-green-600 hover:bg-green-700" data-testid="grade-submit-btn">Submit Grade</Button>
                    </div>
                  </DialogContent>
                </Dialog>
                {assignments.map(a => (
                  <div key={a.id} className="glass-card rounded-xl p-5 border border-slate-200" data-testid={`asgn-card-${a.id}`}>
                    <div className="flex items-start justify-between">
                      <div><div className="flex items-center gap-2"><h3 className="font-semibold">{a.title}</h3><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status === 'graded' ? 'bg-green-100 text-green-700' : a.status === 'submitted' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-amber-100 text-amber-700'}`}>{a.status}</span></div><p className="text-sm text-slate-500 font-semibold mt-1">{a.description}</p></div>
                      {a.status === 'submitted' && <Button size="sm" className="bg-green-600 text-xs" onClick={() => { setGradingAsgn(a); setGradeDialogOpen(true); }} data-testid={`grade-btn-${a.id}`}><Star className="w-3 h-3 mr-1" /> Grade</Button>}
                    </div>
                    <div className="flex gap-3 mt-3 text-xs text-slate-500 font-semibold"><span>To: <span className="text-slate-600 font-semibold font-medium">{a.student_name}</span></span>{a.due_date && <span>Due: {a.due_date}</span>}</div>
                  </div>
                ))}
              </div>
            )}

            {/* === REPORTS === */}
            {activeSection === 'reports' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <h1 className="text-2xl font-bold text-black">Student Reports</h1>
                {teacherReports.length === 0 ? (
                  <div className="glass-card rounded-xl p-12 text-center border"><ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-slate-500 font-semibold">No reports generated yet</p></div>
                ) : teacherReports.map(r => (
                  <div key={r.id} className="glass-card rounded-xl p-5 border border-slate-200 hover:shadow-md cursor-pointer transition-shadow" data-testid={`report-card-${r.id}`}
                    onClick={async () => { try { const res = await api.get(`/report/${r.id}`); setCurrentReport(res.data); setReportViewOpen(true); } catch { toast.error('Failed'); } }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-emerald-700 font-bold" /></div><div><h3 className="font-semibold">{r.student_name}</h3><p className="text-xs text-slate-500 font-semibold">{new Date(r.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div></div>
                      <div className="flex items-center gap-2"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${r.overall_rating === 'Excellent' ? 'bg-green-100 text-green-700' : r.overall_rating === 'Good' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-amber-100 text-amber-700'}`}>{r.overall_rating}</span>{r.shared_with_parent && <Share2 className="w-4 h-4 text-indigo-500" />}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}


            {/* === QUIZ MANAGER === */}
            {activeSection === 'quizzes' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl">
                <QuizManager />
              </div>
            )}

            {/* === REPORT CARD === */}
            {activeSection === 'report-cards-monthly' && !showProfile && (
              <TeacherReportCardManager />
            )}

            {activeSection === 'report-card' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold text-black">Generate Report Card</h1>
                  <Button onClick={() => setReportCardOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-report-card-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Report Card
                  </Button>
                </div>

                {/* Generate Dialog */}
                <Dialog open={reportCardOpen} onOpenChange={setReportCardOpen}>
                  <DialogContent className="bg-white border border-indigo-200 shadow-xl max-w-lg">
                    <DialogHeader><DialogTitle className="text-black font-bold">Generate Report Card</DialogTitle><DialogDescription className="text-gray-600">Select a student and provide your feedback</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div><Label className="text-black font-bold">Student</Label>
                        <Select value={reportCardData.student_id} onValueChange={v => setReportCardData(p => ({...p, student_id: v}))}>
                          <SelectTrigger data-testid="rc-student-select" className="border-2 border-gray-300 text-black"><SelectValue placeholder="Select student" /></SelectTrigger>
                          <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-black font-bold">Teacher Feedback</Label>
                        <Textarea value={reportCardData.teacher_feedback} onChange={e => setReportCardData(p => ({...p, teacher_feedback: e.target.value}))} placeholder="Overall performance, strengths, observations..." rows={3} data-testid="rc-feedback" className="border-2 border-gray-300 text-black" />
                      </div>
                      <div><Label className="text-black font-bold">Areas of Improvement</Label>
                        <Textarea value={reportCardData.areas_of_improvement} onChange={e => setReportCardData(p => ({...p, areas_of_improvement: e.target.value}))} placeholder="Topics to focus on, practice areas..." rows={3} data-testid="rc-improvement" className="border-2 border-gray-300 text-black" />
                      </div>
                      <Button onClick={handleGenerateReportCard} disabled={generatingRC} className="w-full bg-indigo-600 hover:bg-indigo-700 text-black font-bold" data-testid="generate-rc-btn">
                        {generatingRC ? 'Generating...' : 'Generate Report Card'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Viewing a Report Card */}
                {viewingRC && (
                  <div className="glass-card rounded-xl overflow-hidden shadow-sm" data-testid="report-card-view">
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-bold text-slate-900">Monthly Report for {viewingRC.month_label}</h2>
                          <p className="text-indigo-200 text-sm">CodeITT Academy</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-900 font-bold">{viewingRC.student_name}</p>
                          <p className="text-indigo-200 text-xs">{new Date(viewingRC.generated_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 space-y-5">
                      {/* Stats Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-400/30">
                          <div className="text-3xl font-bold text-emerald-700">{viewingRC.total_classes_completed}</div>
                          <div className="text-xs text-slate-500 font-bold mt-1">Total Classes</div>
                        </div>
                        <div className="text-center p-4 bg-blue-500/10 rounded-xl border border-blue-400/30">
                          <div className="text-3xl font-bold text-blue-600">{viewingRC.classes_this_month}</div>
                          <div className="text-xs text-slate-500 font-bold mt-1">This Month</div>
                        </div>
                        <div className="text-center p-4 bg-purple-500/10 rounded-xl border border-purple-400/30">
                          <div className="text-3xl font-bold text-purple-600">{viewingRC.projects_completed?.length || 0}</div>
                          <div className="text-xs text-slate-500 font-bold mt-1">Projects</div>
                        </div>
                      </div>
                      {/* Classes completed this month */}
                      {(viewingRC.classes_this_month_detail?.length > 0 || viewingRC.classes_this_month > 0) && (
                        <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-200/50">
                          <p className="text-sm font-bold text-blue-900 mb-3">Classes Completed in {viewingRC.month_label}</p>
                          {viewingRC.classes_this_month_detail?.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-blue-200/50">
                                    <th className="text-left py-2 px-3 font-bold text-blue-700">#</th>
                                    <th className="text-left py-2 px-3 font-bold text-blue-700">Date</th>
                                    <th className="text-left py-2 px-3 font-bold text-blue-700">Time</th>
                                    <th className="text-left py-2 px-3 font-bold text-blue-700">Course</th>
                                    <th className="text-left py-2 px-3 font-bold text-blue-700">Topic</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {viewingRC.classes_this_month_detail.map((cls, idx) => (
                                    <tr key={`${cls.date}-${cls.time}-${idx}`} className="border-b border-blue-100/50 last:border-0">
                                      <td className="py-2 px-3 font-bold text-black">{idx + 1}</td>
                                      <td className="py-2 px-3 font-bold text-black">{cls.date ? new Date(cls.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}</td>
                                      <td className="py-2 px-3 font-bold text-black">{cls.time || '-'}</td>
                                      <td className="py-2 px-3 font-bold text-black">{cls.course_name || '-'}</td>
                                      <td className="py-2 px-3 font-bold text-black">{cls.topic_name || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-600 font-bold">{viewingRC.classes_this_month} class(es) completed this month</p>
                          )}
                        </div>
                      )}
                      {/* Curriculum */}
                      {viewingRC.curriculum_names?.length > 0 && (
                        <div><p className="text-sm font-bold text-slate-700 mb-2">Curriculum</p>
                          <div className="flex flex-wrap gap-2">{viewingRC.curriculum_names.map((c, i) => <span key={c} className="px-3 py-1 bg-indigo-500/10 text-indigo-700 rounded-full text-xs font-bold border border-indigo-200">{c}</span>)}</div>
                        </div>
                      )}
                      {/* Projects */}
                      {viewingRC.projects_completed?.length > 0 && (
                        <div><p className="text-sm font-bold text-slate-700 mb-2">Projects Completed</p>
                          <div className="space-y-1">{viewingRC.projects_completed.map((p, i) => (
                            <div key={i} className="flex items-center gap-2 p-2 bg-slate-500/10 rounded-lg text-sm"><CheckCircle className="w-4 h-4 text-emerald-500" /><span className="font-bold">{p.title}</span><span className="text-xs text-slate-500 font-bold">{p.course} &middot; {p.category}</span></div>
                          ))}</div>
                        </div>
                      )}
                      {/* Feedback */}
                      <div className="p-4 bg-teal-500/10 rounded-xl border border-teal-200">
                        <p className="text-sm font-bold text-teal-900 mb-1">Teacher Feedback</p>
                        <p className="text-slate-700 font-bold text-sm">{viewingRC.teacher_feedback}</p>
                      </div>
                      {viewingRC.areas_of_improvement && (
                        <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-400/30">
                          <p className="text-sm font-bold text-amber-900 mb-1">Areas of Improvement</p>
                          <p className="text-slate-700 font-bold text-sm">{viewingRC.areas_of_improvement}</p>
                        </div>
                      )}
                      <p className="text-xs text-slate-500 font-bold text-center">Generated by {viewingRC.teacher_name} &middot; Shared with parent</p>
                      <Button variant="outline" size="sm" onClick={() => setViewingRC(null)} className="w-full">Close</Button>
                    </div>
                  </div>
                )}

                {/* Previous Report Cards */}
                {reportCards.length > 0 && !viewingRC && (
                  <div className="space-y-3">
                    <h2 className="font-semibold text-slate-700 font-semibold text-sm">Previous Report Cards</h2>
                    {reportCards.map(rc => (
                      <button key={rc.id} onClick={() => setViewingRC(rc)} className="w-full glass-card rounded-xl p-4 text-left hover:border-indigo-300 hover:shadow-md transition-all" data-testid={`rc-${rc.id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center"><Award className="w-5 h-5 text-indigo-600" /></div>
                            <div>
                              <p className="font-bold text-black">{rc.student_name}</p>
                              <p className="text-xs text-slate-500 font-bold">Monthly Report for {rc.month_label} &middot; {rc.total_classes_completed} classes</p>
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-500 font-semibold" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {reportCards.length === 0 && !viewingRC && (
                  <div className="glass-card rounded-xl p-12 text-center">
                    <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-semibold">No report cards generated yet</p>
                    <p className="text-xs text-slate-500 font-semibold mt-1">Generate a report card for one of your students</p>
                  </div>
                )}
              </div>
            )}


            {/* === AVAILABILITY (new scheduler v2) === */}
            {activeSection === 'availability' && !showProfile && (
              <div className="p-6 max-w-full">
                <TeacherAvailability />
              </div>
            )}

            {/* === SCHEDULE REQUESTS === */}
            {activeSection === 'schedule-requests' && !showProfile && (
              <div className="p-6 max-w-full">
                <ScheduleRequestsPanel />
              </div>
            )}

            {/* === MY FILES === */}
            {activeSection === 'my-files' && !showProfile && <MyFilesPage />}
            {activeSection === 'recordings' && !showProfile && <TeacherRecordingsSection />}

            {/* === CALENDAR === */}
            {activeSection === 'calendar' && !showProfile && (
              <div className="p-6 max-w-full space-y-6">
                {/* V2 Calendar Header */}
                <div className="v2-page-header">
                  <div>
                    <h1 className="v2-page-title" data-testid="calendar-title">
                      {(user?.name || 'Your').split(' ')[0]}&apos;s CodeITT Calendar
                    </h1>
                    <p className="v2-page-subtitle">Plan, manage and view your teaching schedule.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={openCalendarInNewWindow} className="text-xs border-slate-200" data-testid="open-cal-window">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> New Window
                    </Button>
                    <span className="text-xs text-slate-500 font-semibold">IST: {getCurrentTimeInTZ('IST')}</span>
                    <button
                      onClick={() => setAvailabilityChooserOpen(true)}
                      className="v2-cta"
                      data-testid="add-availability-btn"
                    >
                      <Plus className="w-4 h-4" /> Add Availability
                    </button>
                  </div>
                </div>

                {leaveMarkingMode && (
                  <div className="px-4 py-3 rounded-xl bg-rose-50 text-rose-700 border border-rose-200 flex items-center justify-between">
                    <span className="text-sm font-semibold">Click 30-min time slots to mark leave. Selected: {selectedLeaveSlots.length} slot(s)</span>
                    <div className="flex items-center gap-2">
                      {selectedLeaveSlots.length > 0 && (
                        <Button size="sm" onClick={() => setLeaveDialogOpen(true)} className="bg-rose-500 hover:bg-rose-600 text-white text-xs" data-testid="confirm-leave-btn">
                          Submit Leave Request
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { setLeaveMarkingMode(false); setSelectedLeaveSlots([]); }} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                )}

                {availMarkingMode && (
                  <div className={`px-4 py-3 rounded-xl text-sm font-semibold flex items-center justify-between ${availabilityType === 'long_term' ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
                    <span>Click time slots to mark as {availabilityType === 'long_term' ? 'recurring' : 'additional'} availability. {availabilityType === 'long_term' ? 'These repeat every week.' : 'One-time slots.'}</span>
                    <div className="flex items-center gap-2">
                      <Button onClick={saveAvailabilitySlots} disabled={savingAvail || selectedAvailSlots.size === 0} size="sm" className={`${availabilityType === 'long_term' ? 'bg-violet-600 hover:bg-violet-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`} data-testid="save-avail-btn">
                        <Check className="w-3.5 h-3.5 mr-1" />{savingAvail ? 'Saving…' : `Save ${selectedAvailSlots.size} slot(s)`}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { toggleAvailMarking(availabilityType); }} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                )}

                {/* KPI Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="v2-kpi" data-testid="kpi-upcoming">
                    <div className="v2-kpi-label">Upcoming Classes</div>
                    <div className="v2-kpi-value">{teacherSchedule.upcoming_classes.length}</div>
                  </div>
                  <div className="v2-kpi" data-testid="kpi-avail-slots">
                    <div className="v2-kpi-label">Available Slots</div>
                    <div className="v2-kpi-value">{availSlots.length}</div>
                  </div>
                  <div className="v2-kpi" data-testid="kpi-avg-rating">
                    <div className="v2-kpi-label">Avg Rating</div>
                    <div className="v2-kpi-value">{studentRatings.averages?.overall || '—'}<span className="text-slate-400 text-2xl">/10</span></div>
                  </div>
                </div>

                {/* Weekly Calendar Grid */}
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-slate-500/10">
                    <button onClick={() => shiftWeek(-1)} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="prev-week"><ChevronLeft className="w-4 h-4" /></button>
                    <h3 className="font-bold text-black text-sm">
                      {calendarWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    </h3>
                    <button onClick={() => shiftWeek(1)} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="next-week"><ChevronRight className="w-4 h-4" /></button>
                  </div>
                  <div className="overflow-x-auto">
                    <div className="min-w-[700px]">
                      {/* Day headers */}
                      <div className="grid grid-cols-8 border-b border-slate-200">
                        <div className="p-3 text-sm font-bold text-slate-500 font-semibold text-center">Time</div>
                        {getWeekDays().map((d, i) => {
                          const isToday = formatDate(d) === formatDate(new Date());
                          return (
                            <div key={i} className={`p-3 text-center border-l border-slate-200 ${isToday ? 'bg-blue-500/10' : ''}`}>
                              <div className="text-xs font-bold text-slate-500 font-semibold uppercase">{dayNames[i]}</div>
                              <div className={`text-lg font-extrabold ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>{d.getDate()}</div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Time grid */}
                      {(leaveMarkingMode ? calendarSlots : calendarHours.map(h => ({ hour: h, min: 0 }))).map(slot => {
                        const hour = slot.hour;
                        const min = slot.min || 0;
                        const timeLabel = min === 0 ? (hour > 12 ? `${hour-12} PM` : hour === 12 ? '12 PM' : `${hour} AM`) : `${hour > 12 ? hour-12 : hour}:30`;
                        return (
                        <div key={`${hour}-${min}`} className={`grid grid-cols-8 border-b border-gray-50 ${leaveMarkingMode ? 'min-h-[36px]' : 'min-h-[60px]'}`}>
                          <div className="p-1.5 text-sm font-semibold text-slate-500 font-semibold text-right pr-3 pt-2">{timeLabel}</div>
                          {getWeekDays().map((d, di) => {
                            const dateStr = formatDate(d);
                            const hourStr = String(hour).padStart(2, '0');
                            const minStr = String(min).padStart(2, '0');
                            const timeStr = `${hourStr}:${minStr}`;
                            const classesInSlot = teacherSchedule.upcoming_classes.filter(c => c.date === dateStr && c.time && c.time.startsWith(hourStr));
                            const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][d.getDay()];
                            const slotKey = availabilityType === 'long_term' ? `${dayName}|${hourStr}` : `${dateStr}|${hourStr}`;
                            const isSelected = selectedAvailSlots.has(slotKey);
                            const isAvailable = availSlots.some(s =>
                              (s.type === 'long_term' && s.day_of_week === dayName && s.start_time && parseInt(s.start_time.split(':')[0]) === hour) ||
                              (s.type !== 'long_term' && s.date === dateStr && s.start_time && parseInt(s.start_time.split(':')[0]) === hour)
                            );
                            const leaveSlotKey = `${dateStr}|${timeStr}`;
                            const isLeaveSelected = selectedLeaveSlots.includes(leaveSlotKey);
                            return (
                              <div key={di}
                                className={`border-l border-gray-50 p-0.5 relative ${leaveMarkingMode ? 'min-h-[32px]' : 'min-h-[48px]'} transition-colors cursor-pointer
                                  ${leaveMarkingMode && isLeaveSelected ? 'bg-red-100 border-red-400/30' : ''}
                                  ${availMarkingMode && isSelected ? (availabilityType === 'long_term' ? 'bg-teal-100' : 'bg-blue-100') : ''}
                                  ${!availMarkingMode && !leaveMarkingMode && isAvailable && classesInSlot.length === 0 ? 'bg-blue-500/10' : ''}
                                  ${leaveMarkingMode ? 'hover:bg-red-500/10' : availMarkingMode ? 'hover:bg-gray-100' : ''}`}
                                onClick={() => {
                                  if (leaveMarkingMode) toggleLeaveSlot(leaveSlotKey);
                                  else if (availMarkingMode && classesInSlot.length === 0) toggleSlotSelection(slotKey);
                                }}
                                data-testid={`cal-slot-${dateStr}-${hourStr}${min > 0 ? '-30' : ''}`}
                              >
                                {leaveMarkingMode && (
                                  <div className="absolute top-0.5 right-0.5">
                                    <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${isLeaveSelected ? 'bg-red-500/100 border-red-500' : 'border-gray-300 bg-white'}`}>
                                      {isLeaveSelected && <Check className="w-2 h-2 text-slate-900" />}
                                    </div>
                                  </div>
                                )}
                                {availMarkingMode && !leaveMarkingMode && classesInSlot.length === 0 && min === 0 && (
                                  <div className="absolute top-1 right-1">
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? (availabilityType === 'long_term' ? 'bg-teal-600 border-teal-600' : 'bg-blue-600 border-blue-600') : 'border-gray-300 bg-white'}`}>
                                      {isSelected && <Check className="w-2.5 h-2.5 text-slate-900" />}
                                    </div>
                                  </div>
                                )}
                                {min === 0 && classesInSlot.map(cls => {
                                  const isRecurring = cls.schedule_type === 'recurring' || cls.schedule_id;
                                  return (
                                    <div key={cls.id} className={`text-xs px-2 py-1.5 rounded-xl mb-0.5 shadow-sm ${isRecurring ? 'bg-blue-100 text-blue-700 font-bold border border-blue-300' : 'bg-orange-100 text-orange-800 border border-orange-300'}`} data-testid={`cal-class-${cls.id}`} title={`${cls.student_name} - ${cls.time} ${cls.timezone}`}>
                                      <div className="font-bold truncate">{cls.student_name}</div>
                                      <div className="flex items-center justify-between">
                                        <span className={`text-[10px] ${isRecurring ? 'text-blue-600' : 'text-orange-600'}`}>{cls.time}</span>
                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-lg ${isRecurring ? 'bg-blue-200 text-blue-600 font-bold' : 'bg-orange-200 text-orange-700'}`}>{isRecurring ? 'Recurring' : 'Additional'}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                                {!availMarkingMode && !leaveMarkingMode && isAvailable && classesInSlot.length === 0 && min === 0 && (() => {
                                  const matchedSlot = availSlots.find(s =>
                                    (s.type === 'long_term' && s.day_of_week === dayName && s.start_time && parseInt(s.start_time.split(':')[0]) === hour) ||
                                    (s.type !== 'long_term' && s.date === dateStr && s.start_time && parseInt(s.start_time.split(':')[0]) === hour)
                                  );
                                  const slotType = matchedSlot?.type === 'long_term' ? 'Recurring' : 'Additional';
                                  const isRecSlot = slotType === 'Recurring';
                                  return (
                                    <div className={`text-xs px-2 py-1.5 rounded-xl shadow-sm ${isRecSlot ? 'bg-blue-100 text-blue-600 font-bold border border-blue-300' : 'bg-orange-100 text-orange-700 border border-orange-300'}`}>
                                      <div className="font-bold">Available</div>
                                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-lg ${isRecSlot ? 'bg-blue-200 text-blue-600 font-bold' : 'bg-orange-200 text-orange-700'}`}>{slotType}</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          })}
                        </div>
                      )})}
                    </div>
                  </div>
                </div>

                {/* Demo Classes */}
                {demoClasses.length > 0 && (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-black/10 bg-amber-500/10"><h2 className="font-bold text-white text-sm">My Demo Classes</h2></div>
                    <div className="divide-y divide-gray-100">
                      {demoClasses.map(d => (
                        <div key={d.id} className="px-4 py-3 flex items-center justify-between" data-testid={`demo-class-${d.id}`}>
                          <div>
                            <p className="font-bold text-black text-sm">{d.student_name}</p>
                            <p className="text-xs text-slate-500 font-semibold">{d.date} at {d.time}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${d.status === 'completed' ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-amber-100 text-amber-700'}`}>{d.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Schedule - Week/Month */}
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="px-6 py-3 border-b border-black/10 flex items-center justify-between">
                    <h2 className="font-bold text-slate-900 text-sm">Upcoming Schedule</h2>
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button onClick={() => setUpcomingViewMode('week')} className={`px-3 py-1 text-xs font-medium rounded-md ${upcomingViewMode === 'week' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 font-semibold'}`} data-testid="upcoming-week-btn">This Week</button>
                      <button onClick={() => setUpcomingViewMode('month')} className={`px-3 py-1 text-xs font-medium rounded-md ${upcomingViewMode === 'month' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 font-semibold'}`} data-testid="upcoming-month-btn">This Month</button>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {(() => {
                      const now = new Date();
                      const filtered = teacherSchedule.upcoming_classes.filter(cls => {
                        const cd = new Date(cls.date + 'T00:00:00');
                        if (upcomingViewMode === 'week') {
                          const weekEnd = new Date(calendarWeekStart);
                          weekEnd.setDate(weekEnd.getDate() + 7);
                          return cd >= calendarWeekStart && cd < weekEnd;
                        } else {
                          return cd.getMonth() === now.getMonth() && cd.getFullYear() === now.getFullYear();
                        }
                      });
                      if (filtered.length === 0) return (
                        <div className="px-6 py-8 text-center text-slate-500 font-semibold"><Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">No classes {upcomingViewMode === 'week' ? 'this week' : 'this month'}</p></div>
                      );
                      return filtered.map(cls => (
                        <div key={cls.id} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-500/10" data-testid={`sched-class-${cls.id}`}>
                          <div className="w-10 text-center flex-shrink-0">
                            <div className="text-base font-bold text-slate-900">{new Date(cls.date + 'T00:00:00').getDate()}</div>
                            <div className="text-[9px] text-slate-500 font-semibold uppercase">{new Date(cls.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-black text-sm truncate">{cls.student_name}</p>
                            <p className="text-xs text-slate-500 font-semibold">{cls.time} {cls.timezone}</p>
                          </div>
                          {cls.meeting_link && (
                            isJoinEnabled(cls.date, cls.time || cls.start_time) ? (
                              <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer" className="px-2 py-1 bg-teal-600 text-white rounded text-xs hover:bg-teal-700 flex items-center gap-1"><Video className="w-3 h-3" />Join</a>
                            ) : (
                              <span className="px-2 py-1 bg-gray-200 text-slate-500 font-semibold rounded text-xs flex items-center gap-1 cursor-not-allowed" title="Available 10 min before"><Video className="w-3 h-3" />Join</span>
                            )
                          )}
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Active Students with Ratings */}
                {activeStudents.length > 0 && (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="px-6 py-3 border-b border-black/10"><h2 className="font-bold text-slate-900 text-sm">Active Students</h2></div>
                    <div className="divide-y divide-gray-100">
                      {activeStudents.map(s => {
                        const rating = studentRatings.ratings?.find(r => r.student_id === s.id);
                        return (
                          <div key={s.id} className="px-6 py-3 flex items-center gap-3" data-testid={`active-student-${s.id}`}>
                            <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm">{(s.name || '?')[0]}</div>
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-black text-sm truncate">{s.name}</p>
                              <p className="text-xs text-slate-500 font-semibold">{s.timezone || 'IST'} | {s.grade || ''}</p>
                            </div>
                            <div className="flex items-center gap-1 text-amber-500">
                              <Star className="w-3.5 h-3.5 fill-amber-400" />
                              <span className="text-xs font-bold">{rating?.average || '—'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Ratings Summary */}
                {studentRatings.total_ratings > 0 && (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="px-6 py-3 border-b border-black/10"><h2 className="font-bold text-slate-900 text-sm">Student Ratings ({studentRatings.total_ratings})</h2></div>
                    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                      {[{ key: 'explanation_quality', label: 'Explanation' },{ key: 'class_engagement', label: 'Engagement' },{ key: 'learning_new', label: 'Learning' },{ key: 'pace_comfort', label: 'Pace' },{ key: 'recommend_teacher', label: 'Recommend' },{ key: 'overall', label: 'Overall', highlight: true }].map(item => (
                        <div key={item.key} className={`text-center p-3 rounded-xl ${item.highlight ? 'bg-teal-500/10 border border-teal-200' : 'bg-slate-500/10'}`}>
                          <div className={`text-xl font-bold ${item.highlight ? 'text-teal-700' : 'text-slate-900'}`}>{studentRatings.averages[item.key] || 0}</div>
                          <div className="text-[10px] text-slate-500 font-semibold mt-0.5">{item.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Leave Requests History */}
                {leaveRequests.length > 0 && (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="px-6 py-3 border-b border-black/10"><h2 className="font-bold text-slate-900 text-sm">My Leave Requests</h2></div>
                    <div className="divide-y divide-gray-100">
                      {leaveRequests.map(req => (
                        <div key={req.id} className="px-6 py-3 flex items-center justify-between" data-testid={`leave-req-${req.id}`}>
                          <div>
                            <p className="text-sm font-bold text-black">{req.slots?.length || 0} slot(s) &mdash; {req.reason || 'No reason'}</p>
                            <p className="text-xs text-slate-500 font-semibold">{new Date(req.created_at).toLocaleDateString()} &middot; {req.slots?.map(s => s.date).filter((v,i,a) => a.indexOf(v) === i).join(', ')}</p>
                          </div>
                          <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${req.status === 'pending' ? 'bg-amber-100 text-amber-700' : req.status === 'approved' ? 'bg-emerald-100 text-emerald-700 font-bold' : 'bg-red-100 text-red-700'}`}>
                            {req.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* LEAVE REQUEST DIALOG */}
            <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
              <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                <DialogHeader>
                  <DialogTitle>Apply for Leave</DialogTitle>
                  <DialogDescription>{selectedLeaveSlots.length} slot(s) selected</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="text-sm text-slate-600 font-semibold">
                    <p className="font-medium mb-2">Selected Slots:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {selectedLeaveSlots.map(s => {
                        const [date, time] = s.split('|');
                        return <div key={s} className="text-xs bg-red-500/10 text-red-700 px-2 py-1 rounded">{date} at {time}</div>;
                      })}
                    </div>
                  </div>
                  <div>
                    <Label>Reason for Leave</Label>
                    <Textarea value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} placeholder="Describe why you need leave..." rows={3} data-testid="leave-reason-input" />
                  </div>
                  <Button onClick={handleApplyLeave} disabled={submittingLeave} className="w-full bg-orange-500/100 hover:bg-orange-600 text-white" data-testid="submit-leave-btn">
                    {submittingLeave ? 'Submitting...' : 'Submit Leave Request'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* RESCHEDULE DIALOG */}
            <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
              <DialogContent className="bg-white border border-gray-200 shadow-xl max-w-md">
                <DialogHeader><DialogTitle>Reschedule Class</DialogTitle><DialogDescription>{rescheduleTarget?.student_name} — {rescheduleTarget?.date} at {rescheduleTarget?.time}. The Zoom meeting will be updated automatically.</DialogDescription></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>New Date</Label><Input type="date" value={rescheduleData.proposed_date} onChange={e => setRescheduleData(p => ({...p, proposed_date: e.target.value}))} data-testid="reschedule-date" /></div>
                    <div><Label>New Time</Label><Input type="time" value={rescheduleData.proposed_time} onChange={e => setRescheduleData(p => ({...p, proposed_time: e.target.value}))} data-testid="reschedule-time" /></div>
                  </div>
                  <Button onClick={handleRequestReschedule} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="submit-reschedule-btn">Confirm Reschedule</Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* === STUDENT GROUPS === */}
            {activeSection === 'student-groups' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold text-black">Student Groups</h1>
                  <Button className="bg-teal-600 hover:bg-teal-700 text-xs" disabled data-testid="create-group-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Create Group
                  </Button>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center"><UsersRound className="w-6 h-6 text-teal-600" /></div>
                    <div><h2 className="font-bold text-slate-900">Organize Your Students</h2><p className="text-sm text-slate-500 font-semibold">Group students by level, course, or batch for easier management</p></div>
                  </div>
                </div>
                {students.length > 0 ? (
                  <div className="glass-card rounded-xl overflow-hidden">
                    <div className="px-6 py-4 border-b border-black/10"><h2 className="font-bold text-slate-900">All Students ({students.length})</h2></div>
                    <div className="divide-y divide-gray-100">
                      {students.map(s => (
                        <div key={s.id} className="px-6 py-3 flex items-center gap-3" data-testid={`group-student-${s.id}`}>
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center"><User className="w-4 h-4 text-teal-600" /></div>
                          <div className="flex-1"><p className="font-medium text-sm text-slate-900">{s.name}</p><p className="text-xs text-slate-500 font-semibold">{s.email}</p></div>
                          <span className="px-2 py-0.5 bg-gray-100 text-slate-600 font-semibold text-[10px] font-medium rounded-full">{s.enrollments?.length || 0} courses</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glass-card rounded-xl p-12 text-center border"><UsersRound className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-slate-500 font-semibold">No students assigned yet</p></div>
                )}
                <p className="text-xs text-slate-500 font-semibold text-center">Group creation and batch management features coming soon</p>
              </div>
            )}

            {/* === REFER & EARN === */}
            {/* === DISCUSSIONS === */}
            {activeSection === 'discussions' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <div className="flex items-center justify-between">
                  <h1 className="text-2xl font-bold text-black">Discussions</h1>
                  <Button onClick={() => setNewThreadOpen(true)} className="bg-teal-600 hover:bg-teal-700" data-testid="new-thread-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Thread
                  </Button>
                </div>

                {/* New Thread Dialog */}
                <Dialog open={newThreadOpen} onOpenChange={setNewThreadOpen}>
                  <DialogContent className="bg-white">
                    <DialogHeader><DialogTitle>Start Discussion</DialogTitle><DialogDescription>Choose a student to start a conversation</DialogDescription></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div><Label>Student</Label>
                        <Select value={newThreadData.student_id} onValueChange={v => setNewThreadData(p => ({...p, student_id: v}))}>
                          <SelectTrigger data-testid="thread-student-select"><SelectValue placeholder="Select a student" /></SelectTrigger>
                          <SelectContent>{activeStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.name}{s.timezone ? ` (${s.timezone})` : ''}</SelectItem>)}</SelectContent>
                        </Select>
                        {newThreadData.student_id && (() => { const s = students.find(st => st.id === newThreadData.student_id); return s?.timezone ? (
                          <div className="mt-2 flex items-center gap-1.5 bg-blue-500/10 border border-blue-400/30 rounded-lg px-3 py-2">
                            <Globe className="w-3.5 h-3.5 text-blue-600" />
                            <span className="text-xs font-semibold text-blue-600 font-bold">{s.timezone}</span>
                            <span className="text-xs text-blue-500 font-mono ml-1">{getCurrentTimeInTZ(s.timezone)}</span>
                          </div>
                        ) : null; })()}
                      </div>
                      <div><Label>Subject</Label><Input value={newThreadData.subject} onChange={e => setNewThreadData(p => ({...p, subject: e.target.value}))} placeholder="e.g. Homework help, Project discussion" data-testid="thread-subject" /></div>
                      <div><Label>First Message (optional)</Label><Textarea value={newThreadData.message} onChange={e => setNewThreadData(p => ({...p, message: e.target.value}))} placeholder="Start the conversation..." data-testid="thread-first-message" /></div>
                      <Button onClick={handleCreateThread} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="create-thread-btn">Create Thread</Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {selectedThread ? (
                  <div className="glass-card rounded-xl shadow-sm">
                    <div className="p-4 border-b border-black/10">
                      <button onClick={() => setSelectedThread(null)} className="text-xs text-teal-600 hover:underline mb-1">&larr; Back</button>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-black">{selectedThread.subject}</h3>
                          <p className="text-xs text-slate-500 font-semibold">with {selectedThread.student_name}</p>
                        </div>
                        {(() => { const student = students.find(s => s.id === selectedThread.student_id); return student?.timezone ? (
                          <div className="flex items-center gap-1.5 bg-blue-500/10 border border-blue-400/30 rounded-lg px-2.5 py-1.5" data-testid="thread-tz-badge">
                            <Globe className="w-3.5 h-3.5 text-blue-600" />
                            <div className="text-right">
                              <p className="text-xs font-bold text-blue-600 font-bold">{student.timezone}</p>
                              <p className="text-[10px] text-blue-500 font-mono">{getCurrentTimeInTZ(student.timezone)}</p>
                            </div>
                          </div>
                        ) : null; })()}
                      </div>
                    </div>
                    <div className="p-4 space-y-3 max-h-96 overflow-y-auto" data-testid="teacher-thread-messages">
                      {(selectedThread.messages || []).map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_role === 'teacher' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm ${msg.sender_role === 'teacher' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-slate-800 font-semibold'}`}>
                            <p className="text-xs opacity-70 mb-0.5">{msg.sender_name}</p>
                            <p>{msg.content}</p>
                            <p className="text-[10px] opacity-50 mt-1">{new Date(msg.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="p-4 border-t border-black/10 flex gap-2">
                      <Input value={threadMessage} onChange={e => setThreadMessage(e.target.value)} placeholder="Type a message..." className="flex-1" data-testid="teacher-thread-input" onKeyDown={e => e.key === 'Enter' && handleSendThreadMessage()} />
                      <Button onClick={handleSendThreadMessage} className="bg-teal-600 hover:bg-teal-700" data-testid="teacher-send-msg-btn"><Send className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {discussionThreads.length === 0 ? (
                      <div className="glass-card rounded-xl p-12 text-center">
                        <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-semibold">No discussions yet</p>
                        <p className="text-xs text-slate-500 font-semibold mt-1">Start a thread with one of your students</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {discussionThreads.map(t => (
                          <button key={t.id} onClick={async () => { try { const r = await api.get(`/discussion-threads/${t.id}`); setSelectedThread(r.data); } catch { setSelectedThread(t); } }} className="w-full glass-card rounded-xl p-4 text-left hover:border-teal-300 transition-colors" data-testid={`teacher-thread-${t.id}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-black">{t.subject}</p>
                                <p className="text-sm text-slate-500 font-semibold">with {t.student_name}
                                  {(() => { const s = students.find(st => st.id === t.student_id); return s?.timezone ? <span className="ml-2 text-xs text-blue-600 font-medium">({s.timezone} — {getCurrentTimeInTZ(s.timezone)})</span> : null; })()}
                                </p>
                              </div>
                              <div className="text-right flex items-center gap-2">
                                <p className="text-xs text-slate-500 font-semibold">{(t.messages || []).length} msgs</p>
                                <ChevronRight className="w-4 h-4 text-slate-500 font-semibold" />
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {activeSection === 'refer' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-3xl space-y-6">
                <h1 className="text-2xl font-bold text-black">Refer & Earn</h1>
                <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-white/20  flex items-center justify-center"><UserPlus className="w-8 h-8 text-slate-900" /></div>
                    <div><h2 className="text-2xl font-bold">Earn Extra Income</h2><p className="text-emerald-100">Refer a teacher & earn rewards for every successful referral</p></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                    <div className="bg-slate-100  rounded-xl p-4 text-center border border-black/20"><div className="text-3xl font-bold">0</div><div className="text-xs text-emerald-100 mt-1">Total Referrals</div></div>
                    <div className="bg-slate-100  rounded-xl p-4 text-center border border-black/20"><div className="text-3xl font-bold">0</div><div className="text-xs text-emerald-100 mt-1">Successful</div></div>
                    <div className="bg-slate-100  rounded-xl p-4 text-center border border-black/20"><div className="text-3xl font-bold">₹0</div><div className="text-xs text-emerald-100 mt-1">Earned</div></div>
                  </div>
                </div>
                <div className="glass-card rounded-xl p-6">
                  <h3 className="font-bold text-black mb-4">How It Works</h3>
                  <div className="space-y-4">
                    {[
                      { step: '1', title: 'Share Your Referral', desc: 'Share your unique referral code with fellow teachers' },
                      { step: '2', title: 'They Join CodeITT', desc: 'Your referral signs up and starts teaching' },
                      { step: '3', title: 'You Earn Rewards', desc: 'Receive bonus payouts for every active referral' },
                    ].map(item => (
                      <div key={item.step} className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-emerald-700 font-bold">{item.step}</span></div>
                        <div><h4 className="font-bold text-black text-sm">{item.title}</h4><p className="text-xs text-slate-500 font-semibold">{item.desc}</p></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-slate-500/10 rounded-xl p-4 border text-center">
                  <p className="text-sm text-slate-500 font-semibold">Referral code generation and tracking coming soon</p>
                </div>
              </div>
            )}

            {/* === POLICIES === */}
            {activeSection === 'policies' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-3xl space-y-6">
                <h1 className="text-2xl font-bold text-black">Policies</h1>
                {[
                  { title: 'Class Attendance Policy', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700 font-bold', items: ['Teachers must join class 2 minutes before scheduled time', 'Mark attendance for every class conducted', 'Notify admin at least 4 hours before for cancellations', 'Maintain minimum 95% class completion rate'] },
                  { title: 'Payment & Earnings Policy', icon: IndianRupee, color: 'bg-amber-100 text-amber-600', items: ['Earnings calculated on a monthly basis', 'Demo classes: ₹150 per session', 'Paid classes: ₹300 (India) / ₹450 (USA/Canada) per session', 'Payouts processed by 5th of every month'] },
                  { title: 'Student Interaction Guidelines', icon: Users, color: 'bg-blue-100 text-blue-600', items: ['Maintain professional communication at all times', 'Share progress reports with parents monthly', 'Respond to parent queries within 24 hours', 'No direct payment collection from students/parents'] },
                  { title: 'Content & Curriculum Policy', icon: BookOpen, color: 'bg-purple-100 text-purple-600', items: ['Follow the prescribed curriculum structure', 'Use only approved teaching materials', 'Update class progress after each session', 'Report any curriculum issues to the admin team'] },
                ].map(policy => (
                  <div key={policy.title} className="glass-card rounded-xl overflow-hidden" data-testid={`policy-${policy.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="px-6 py-4 border-b border-black/10 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${policy.color}`}><policy.icon className="w-4.5 h-4.5" /></div>
                      <h2 className="font-bold text-slate-900">{policy.title}</h2>
                    </div>
                    <ul className="px-6 py-4 space-y-2.5">
                      {policy.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700 font-semibold"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" /><span>{item}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* === UPSKILL === */}
            {activeSection === 'upskill' && !showProfile && (
              <div className="p-3 sm:p-6 max-w-4xl space-y-6">
                <div>
                  <h1 className="text-2xl font-bold text-black">Upskill</h1>
                  <p className="text-sm text-black font-bold mt-1">Free courses to enhance your teaching skills</p>
                </div>
                {upskillSections.map((section) => (
                  <div key={section.title} className="nb-card overflow-hidden" data-testid={`upskill-section-${section.title.toLowerCase().replace(/[\s\/&]+/g, '-')}`}>
                    <div className="px-6 py-4 border-b border-black/10 bg-gradient-to-r from-orange-100/50 to-amber-100/50">
                      <h2 className="font-bold text-black text-base">{section.title}</h2>
                    </div>
                    <div className="p-5 flex flex-wrap gap-3">
                      {section.courses.map((course) => (
                        <button
                          key={course.name}
                          onClick={() => window.open(course.url, '_blank')}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-50 border border-orange-300/50 text-orange-700 font-bold text-sm shadow-sm hover:shadow-md hover:bg-orange-50 hover:scale-[1.03] transition-all"
                          data-testid={`upskill-course-${course.name.toLowerCase().replace(/[\s\/&]+/g, '-')}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          {course.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* === PROFILE === */}
            {showProfile && profile && (
              <div className="p-3 sm:p-6 max-w-3xl space-y-6">
                <h1 className="text-2xl font-bold text-black">Teacher Profile</h1>
                <div className="bg-white/20 rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-gradient-to-r from-[#3B82F6] to-indigo-600 p-8">
                    <div className="flex items-center gap-6">
                      <div className="relative group">
                        <div className="w-20 h-20 rounded-full bg-white/20  flex items-center justify-center overflow-hidden border-2 border-white/30">
                          {profile.profile_photo ? (
                            <img src={profile.profile_photo.startsWith('/api/') ? `${API}${profile.profile_photo}` : profile.profile_photo} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <User className="w-10 h-10 text-slate-900" />
                          )}
                        </div>
                        <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                          <Upload className="w-5 h-5 text-slate-900" />
                          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                            const file = e.target.files[0]; if (!file) return;
                            const fd = new FormData(); fd.append('file', file);
                            try { await api.post('/teacher/profile-photo', fd); toast.success('Photo uploaded!'); fetchProfile(); } catch { toast.error('Upload failed'); }
                          }} data-testid="photo-upload-input" />
                        </label>
                      </div>
                      <div><h2 className="text-2xl font-bold text-black">{profile.name}</h2><p className="text-blue-200">{profile.email}</p></div>
                    </div>
                    {/* Preset avatars */}
                    <div className="mt-4">
                      <p className="text-xs text-blue-200 mb-2 font-bold">Or choose an avatar:</p>
                      <div className="flex gap-2">
                        {[
                          { src: '/assets/avatar_male.png', label: 'Male' },
                          { src: '/assets/avatar_female_1.png', label: 'Female 1' },
                          { src: '/assets/avatar_female_2.png', label: 'Female 2' },
                          { src: '/assets/avatar_female_3.png', label: 'Female 3' },
                        ].map((av, i) => (
                          <button key={i} onClick={async () => {
                            try { await api.post('/teacher/profile-photo/preset', { preset: av.src }); toast.success('Avatar set!'); fetchProfile(); } catch { toast.error('Failed'); }
                          }} className={`w-12 h-12 rounded-full overflow-hidden border-2 hover:border-white transition-all ${profile.profile_photo === av.src ? 'border-white ring-2 ring-white' : 'border-white/30'}`}
                            data-testid={`preset-avatar-${i}`}>
                            <img src={av.src} alt={av.label} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="p-6 space-y-8">
                    {/* Stats Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-amber-50 rounded-xl border border-amber-200"><div className="flex items-center justify-center gap-1"><Star className="w-5 h-5 text-amber-400" /><span className="text-3xl font-bold text-black" data-testid="profile-rating">{profile.rating}</span></div><div className="text-sm text-slate-500 font-semibold mt-1">Rating</div></div>
                      <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200"><div className="text-3xl font-bold text-black" data-testid="profile-classes">{profile.total_classes}</div><div className="text-sm text-slate-500 font-semibold mt-1">Classes</div></div>
                      <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-200"><div className="text-3xl font-bold text-black" data-testid="profile-students">{profile.students_taught}</div><div className="text-sm text-slate-500 font-semibold mt-1">Students</div></div>
                    </div>

                    {/* Intro Video (100 MB) — shown to students before booking */}
                    <div>
                      <h3 className="font-bold text-black mb-2 flex items-center gap-2">
                        <Video className="w-4 h-4 text-indigo-600" /> Intro Video
                        <span className="text-[10px] text-slate-400 font-normal">shown to students on the scheduler</span>
                      </h3>
                      {profile.intro_video_file_id ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50" data-testid="intro-video-current">
                          <video controls src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/uploads/${profile.intro_video_file_id}?auth=${localStorage.getItem('token')}`} className="w-40 h-24 rounded-md bg-black" />
                          <div className="flex-1">
                            <p className="text-xs font-bold text-indigo-800">Intro video is live ✓</p>
                            <p className="text-[11px] text-slate-500 mb-2">Students see this on the booking sidebar.</p>
                            <Button size="sm" variant="outline" onClick={async () => {
                              try {
                                await api.post('/teacher/intro-video', { file_id: null });
                                toast.success('Intro video removed');
                                if (typeof fetchProfile === 'function') fetchProfile();
                              } catch (e) { toast.error('Failed'); }
                            }} className="text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid="remove-intro-video">Remove</Button>
                          </div>
                        </div>
                      ) : (
                        <FileUpload
                          variant="dropzone"
                          context="intro-video"
                          maxSizeMB={100}
                          accept="video/*"
                          onUploaded={async (f) => {
                            try {
                              await api.post('/teacher/intro-video', { file_id: f.id });
                              toast.success('Intro video saved — visible to students');
                              if (typeof fetchProfile === 'function') fetchProfile();
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
                          }}
                          label="Upload 30-60s welcome video (max 100 MB)"
                          dataTestid="intro-video-upload"
                        />
                      )}
                    </div>


                    {/* Vertical */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-black">Vertical</h3>
                        <Button variant="ghost" size="sm" onClick={() => setEditingVertical(!editingVertical)} className="text-slate-500 hover:text-slate-900" data-testid="edit-vertical-btn">{editingVertical ? 'Done' : 'Edit'}</Button>
                      </div>
                      {!editingVertical ? (
                        <div>
                          {profile.vertical ? (
                            <span className="px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg text-sm font-bold border border-indigo-200" data-testid="profile-vertical">{profile.vertical}</span>
                          ) : (
                            <p className="text-sm text-slate-400">No vertical selected</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2" data-testid="vertical-options">
                          {['Coding', 'Robotics', 'Mathematics', 'AI', 'IELTS', 'Spoken Mastery'].map(v => (
                            <button key={v} onClick={() => { handleUpdateProfile({ vertical: v }); setEditingVertical(false); }}
                              className={`px-4 py-2 rounded-lg text-sm font-bold border-2 transition-all ${profile.vertical === v ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-indigo-300'}`}>
                              {v}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Bio / Description */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-black">About Me</h3>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingBio(!editingBio); if (!editingBio) setBioInput(profile.bio || ''); }} className="text-slate-500 hover:text-slate-900" data-testid="edit-bio-btn">{editingBio ? 'Cancel' : 'Edit'}</Button>
                      </div>
                      {!editingBio ? (
                        <div>
                          {profile.bio ? (
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="profile-bio">{profile.bio}</div>
                          ) : (
                            <p className="text-sm text-slate-400">No bio added yet. Tell students about yourself!</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <textarea
                            value={bioInput}
                            onChange={e => setBioInput(e.target.value)}
                            rows={6}
                            className="w-full rounded-xl border border-slate-300 p-4 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            placeholder="Write about your teaching experience, qualifications, approach, and what makes your classes special..."
                            data-testid="bio-textarea"
                          />
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">{bioInput.length}/1000 characters</span>
                            <Button size="sm" onClick={() => { handleUpdateProfile({ bio: bioInput.slice(0, 1000) }); setEditingBio(false); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-bio-btn">Save</Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Skills */}
                    <div>
                      <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-black">Skills</h3><Button variant="ghost" size="sm" onClick={() => setEditingSkills(!editingSkills)} className="text-slate-500 hover:text-slate-900" data-testid="edit-skills-btn">{editingSkills ? 'Done' : 'Edit'}</Button></div>
                      <div className="flex flex-wrap gap-2">
                        {(profile.skills || []).map((s, i) => (
                          <span key={i} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200 flex items-center gap-1.5">
                            {s}
                            {editingSkills && (
                              <button onClick={() => handleUpdateProfile({ skills: (profile.skills || []).filter((_, idx) => idx !== i) })} className="text-red-400 hover:text-red-600">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        {!profile.skills?.length && !editingSkills && <p className="text-sm text-slate-400">No skills added</p>}
                      </div>
                      {editingSkills && (
                        <div className="flex gap-2 mt-3">
                          <Input placeholder="Add skill (e.g., Python, Scratch)" value={skillInput} onChange={e => setSkillInput(e.target.value)} className="border-slate-300" data-testid="skill-input"
                            onKeyDown={e => { if (e.key === 'Enter' && skillInput.trim()) { handleUpdateProfile({ skills: [...(profile.skills || []), skillInput.trim()] }); setSkillInput(''); } }} />
                          <Button size="sm" onClick={() => { if (skillInput.trim()) { handleUpdateProfile({ skills: [...(profile.skills || []), skillInput.trim()] }); setSkillInput(''); } }} className="bg-blue-600" data-testid="add-skill-btn">Add</Button>
                        </div>
                      )}
                    </div>

                    {/* Introduction Video */}
                    <div>
                      <h3 className="font-bold text-black mb-3">Introduction Video</h3>
                      {profile.video_url ? (
                        <div className="space-y-3">
                          <div className="rounded-xl overflow-hidden border border-slate-200 bg-black">
                            <video src={profile.video_url.startsWith('http') ? profile.video_url : `${API}${profile.video_url}`} controls className="w-full max-h-80" data-testid="profile-video" />
                          </div>
                          <label className="cursor-pointer inline-block">
                            <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={handleVideoUpload} className="hidden" data-testid="video-replace-input" />
                            <span className="text-sm text-blue-600 hover:text-blue-700 font-semibold cursor-pointer">Replace video</span>
                          </label>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center bg-slate-50">
                          <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                          <p className="text-sm text-slate-500 mb-1">Upload a short introduction video</p>
                          <p className="text-xs text-slate-400 mb-4">MP4, MOV, WEBM — Max 100MB</p>
                          <label className="cursor-pointer">
                            <input type="file" accept="video/mp4,video/quicktime,video/webm" onChange={handleVideoUpload} className="hidden" data-testid="video-upload-input" />
                            <span className={`px-5 py-2.5 rounded-lg text-sm font-bold text-white inline-block ${videoUploading ? 'bg-slate-400' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'}`}>
                              {videoUploading ? 'Uploading...' : 'Choose Video'}
                            </span>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Report View Dialog */}
            <Dialog open={reportViewOpen} onOpenChange={setReportViewOpen}>
              <DialogContent className="max-w-3xl bg-white border border-gray-200 shadow-xl max-h-[90vh] overflow-y-auto">
                {currentReport && (<>
                  <div className="bg-gradient-to-r from-teal-600 to-cyan-600 -m-6 mb-6 p-8 rounded-t-lg">
                    <div className="flex items-center justify-between"><div><h2 className="text-2xl font-bold text-black">Progress Report</h2><p className="text-cyan-200 text-sm">CodeITT Academy</p></div><div className="text-right"><p className="text-black font-bold">{currentReport.student_name}</p><p className="text-cyan-200 text-xs">{new Date(currentReport.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div></div>
                  </div>
                  <div className="flex items-center justify-between p-4 rounded-xl border mb-6" data-testid="report-overall-rating">
                    <div><p className="text-sm text-slate-500 font-semibold">Overall</p><p className="text-xl font-bold">{currentReport.overall_rating}</p></div>
                    <div className={`px-4 py-2 rounded-xl text-lg font-bold ${currentReport.overall_rating === 'Excellent' ? 'bg-green-100 text-green-700' : currentReport.overall_rating === 'Good' ? 'bg-blue-100 text-blue-600 font-bold' : 'bg-amber-100 text-amber-700'}`}>{currentReport.overall_rating === 'Excellent' ? 'A' : currentReport.overall_rating === 'Good' ? 'B' : 'C'}</div>
                  </div>
                  {currentReport.curriculum_summary?.length > 0 && (<div className="mb-6"><h3 className="font-semibold mb-3">Curriculum Progress</h3>{currentReport.curriculum_summary.map((c, i) => (<div key={i} className="p-3 bg-slate-500/10 rounded-lg mb-2"><div className="flex justify-between text-sm mb-1"><span className="font-medium">{c.course} &middot; {c.category}</span><span className="text-teal-600 font-semibold">{c.percentage}%</span></div><Progress value={c.percentage} className="h-2" /></div>))}</div>)}
                  <div className="mb-6"><h3 className="font-semibold mb-3">Assignments</h3><div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[{l:'Total',v:currentReport.assignment_summary?.total},{l:'Submitted',v:currentReport.assignment_summary?.submitted},{l:'Graded',v:currentReport.assignment_summary?.graded},{l:'Avg',v:currentReport.assignment_summary?.average_grade}].map((x,i) => <div key={i} className="text-center p-2 bg-slate-500/10 rounded-lg"><div className="text-lg font-bold">{x.v||0}</div><div className="text-xs text-slate-500 font-semibold">{x.l}</div></div>)}</div></div>
                  {currentReport.teacher_remarks && (<div className="p-4 bg-teal-500/10 rounded-xl border border-teal-200"><h3 className="font-semibold text-teal-900 mb-1 text-sm">Remarks</h3><p className="text-slate-700 font-semibold">{currentReport.teacher_remarks}</p><p className="text-xs text-teal-600 mt-2">— {currentReport.teacher_name}</p></div>)}
                </>)}
              </DialogContent>
            </Dialog>
          </main>
        </div>
      </div>
      {/* Resource Edit Dialog */}
      <Dialog open={resourceEditOpen} onOpenChange={setResourceEditOpen}>
        <DialogContent className="bg-white border border-slate-200 text-slate-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Edit Resources — {resourceTarget?.topic_name}</DialogTitle>
            <DialogDescription className="text-slate-500">Add presentation slides and class notes URLs for this topic</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-slate-700 font-semibold text-sm flex items-center gap-1.5"><MonitorPlay className="w-4 h-4 text-violet-500" /> Presentation URL</Label>
              <Input value={resourceForm.presentation_url} onChange={e => setResourceForm({...resourceForm, presentation_url: e.target.value})} placeholder="https://docs.google.com/presentation/d/..." className="mt-1" data-testid="resource-pres-input" />
            </div>
            <div>
              <Label className="text-slate-700 font-semibold text-sm flex items-center gap-1.5"><FileText className="w-4 h-4 text-amber-500" /> Class Notes URL</Label>
              <Input value={resourceForm.class_notes_url} onChange={e => setResourceForm({...resourceForm, class_notes_url: e.target.value})} placeholder="https://docs.google.com/document/d/..." className="mt-1" data-testid="resource-notes-input" />
            </div>
            <Button onClick={handleSaveResources} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" data-testid="save-resources-btn">Save Resources</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Right utility rail — Profile + Today's Overview + Next Class + Spark (bottom) */}
      <RightUtilityRail sparkMode="teacher">
        {/* Profile Card */}
        {profile && (
          <div className="rounded-xl p-4 bg-white/[0.06] border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border border-white/15">
                {profile.profile_photo ? (
                  <img src={profile.profile_photo.startsWith('/api/') ? `${API}${profile.profile_photo}` : profile.profile_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-slate-200" />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-bold text-white text-sm truncate">{user.name}</p>
                <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
              </div>
            </div>
            <div className="rounded-lg p-2.5 bg-white/[0.04] border border-white/10">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] text-slate-400 font-semibold">Overall Rating</span>
                <span className="text-sm font-bold text-violet-300">{profile.rating}/5</span>
              </div>
              <Progress value={(profile.rating / 5) * 100} className="h-1.5 bg-white/10" />
            </div>
          </div>
        )}

        {/* Today's Overview */}
        <div className="rounded-xl p-4 bg-white/[0.06] border border-white/10">
          <h3 className="text-[13px] font-bold text-white mb-3">Today's Overview</h3>
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 flex items-center justify-center"><BookOpen className="w-3 h-3 text-emerald-300" /></div>
                <span className="text-[12px] text-slate-300 font-medium">Paid Classes</span>
              </div>
              <span className="text-sm font-bold text-white v2-num">{paidCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-amber-500/20 flex items-center justify-center"><GraduationCap className="w-3 h-3 text-amber-300" /></div>
                <span className="text-[12px] text-slate-300 font-medium">Demo Classes</span>
              </div>
              <span className="text-sm font-bold text-white v2-num">{demoCount}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-blue-500/20 flex items-center justify-center"><Users className="w-3 h-3 text-blue-300" /></div>
                <span className="text-[12px] text-slate-300 font-medium">Total Students</span>
              </div>
              <span className="text-sm font-bold text-white v2-num">{students.length}</span>
            </div>
          </div>
        </div>

        {/* Next Class */}
        <div className="rounded-xl p-4 bg-white/[0.06] border border-white/10">
          <h3 className="text-[13px] font-bold text-white mb-2">Next Class</h3>
          {(upcomingClasses.paid_classes || []).slice(0, 1).map(cls => (
            <div key={cls.id} className="rounded-lg p-3 bg-blue-500/10 border border-blue-400/25">
              <p className="font-semibold text-sm text-white truncate">{cls.student_name || 'Student'}</p>
              <p className="text-[11px] text-slate-300 mt-0.5">{cls.date} · {cls.time || cls.start_time}</p>
              {cls.meeting_link && (
                isJoinEnabled(cls.date, cls.time || cls.start_time) ? (
                  <a href={cls.meeting_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center mt-2 w-8 h-8 rounded-full bg-gradient-to-b from-blue-500 to-blue-700 text-white shadow-md hover:from-blue-400 hover:to-blue-600 transition-all" data-testid="sidebar-join-btn"><Video className="w-3.5 h-3.5" /></a>
                ) : (
                  <span className="inline-flex items-center justify-center mt-2 w-8 h-8 rounded-full bg-slate-600/50 text-slate-400 cursor-not-allowed" title="Available 10 min before"><Video className="w-3.5 h-3.5" /></span>
                )
              )}
            </div>
          ))}
          {(upcomingClasses.paid_classes || []).length === 0 && (
            <div className="rounded-lg p-3 bg-white/[0.03] border border-white/10 text-center">
              <p className="text-[12px] text-slate-400">No upcoming classes</p>
            </div>
          )}
        </div>
      </RightUtilityRail>

      {/* Today's Topic Picker */}
      <Dialog open={topicPicker.open} onOpenChange={(v) => !v && setTopicPicker({ open: false, cls: null, options: [], loading: false })}>
        <DialogContent className="max-w-md" data-testid="topic-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" /> Pick Today's Topic
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {topicPicker.cls && (
              <p className="text-xs font-bold text-slate-500">
                For <span className="text-slate-800">{topicPicker.cls.student_name}</span> ·
                {topicPicker.mode === 'locked' ? ' Locked progression' : ' Flexible progression'}
              </p>
            )}
            {topicPicker.loading ? (
              <p className="text-sm text-slate-400 text-center py-6">Loading recommendations…</p>
            ) : topicPicker.options.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No pending topics — module may be complete.</p>
            ) : (
              <div className="space-y-1.5 max-h-80 overflow-y-auto">
                {topicPicker.options.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTopicPick(t)}
                    className={`w-full text-left rounded-lg border-2 px-3 py-2.5 text-sm font-semibold transition-all ${
                      t.recommended
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-indigo-300'
                    }`}
                    data-testid={`topic-option-${t.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{t.title}</span>
                      {t.recommended && (
                        <span className="text-[10px] font-extrabold uppercase tracking-wider bg-indigo-500 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">
                          Recommended
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Class Feedback dialog */}
      <ClassFeedbackForm
        classId={feedbackClassId}
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        onSubmitted={() => { setPendingRefreshKey(k => k + 1); api.get('/teacher/upcoming-classes').then(r => setUpcomingClasses(r.data)).catch(() => {}); }}
      />

      {/* + Add Availability slide-over */}
      <AddAvailabilitySlideOver
        open={availabilityChooserOpen}
        onClose={() => setAvailabilityChooserOpen(false)}
        onRecurring={() => { setLeaveMarkingMode(false); if (availabilityType !== 'long_term' || !availMarkingMode) { toggleAvailMarking('long_term'); } }}
        onAdditional={() => { setLeaveMarkingMode(false); if (availabilityType !== 'additional' || !availMarkingMode) { toggleAvailMarking('additional'); } }}
        onLeave={() => { setAvailMarkingMode(false); setLeaveMarkingMode(true); setSelectedLeaveSlots([]); }}
      />

    </div>
  );
};

export default TeacherDashboard;
